import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    AuthContext,
    IAuthContext,
} from '@deltek/specification-client/core/Configurations/AuthContext'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    ListingMatch,
    LogEventSource,
} from '@deltek/specification-client/core/Models/listingSearch'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import {
    AddManufacturerProductListingElementResult,
    ProjectElementNode,
} from '@deltek/specification-client/core/Models/productListingDetail'
import {
    ProductTypePropertiesInputType,
    ProductTypeSelectionInput,
} from '@deltek/specification-client/core/Models/productType'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import {
    CreateProjectManufacturerProductListingInput,
    DeleteProjectManufacturerProductListingInput,
    GetProjectManufacturerProductListingInput,
} from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { SearchFacet } from '@deltek/specification-client/core/Models/searchFacet'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    CreateProductFlagForReviewMutation,
    RemoveProductFlagForReviewMutation,
} from 'api/graphQL/mutations/product.mutations'
import {
    AddManufacturerProductListingElementsMutation,
    AddManufacturerProductListingInProjectMutation,
    DeleteProjectManufacturerProductListingMutation,
    SetProjectManufacturerProductListingAsBODMutation,
    ToggleMultipleManufacturerSelectionsMutation,
} from 'api/graphQL/mutations/projectNugget.mutations'
import {
    GetProductFlagForReviewQuery,
    ProductCardAutosuggestQuery,
    ProductSearchAutosuggestQuery,
    ProductSearchQuery,
    ProductSearchQueryMinimal,
} from 'api/graphQL/queries/product.queries'
import { GetProjectQuery } from 'api/graphQL/queries/project.queries'
import {
    GetNuggetForProductListingsQuery,
    GetProductTypesWithBODQuery,
    GetProjectElementNodesQuery,
    GetProjectManufacturerProductListingQuery,
    GetProjectNuggetForProductListingsQuery,
} from 'api/graphQL/queries/projectElements.queries'
import {
    setUser,
    userInfo,
    userInfoInitial,
} from 'api/graphQL/reactiveVars/user'
import { isOmWorkspaceUser } from 'api/graphQL/reactiveVars/workspaceContent'
import {
    AutoSuggestFieldType,
    CreateFlagForReviewResponse,
    GetFlagForReviewResponse,
    ProductCardAutosuggestResponse,
    ProductListingFlagForReview,
    ProductSearchAutosuggestResponse,
    ProductSearchResponse,
    RemoveFlaggedForReviewResponse,
    SearchFacetInput,
    SearchFacets,
    SelectedManufacturerListing,
} from 'api/graphQL/types/product.type'
import { UpdatedListingSearchInput } from 'api/graphQL/types/project.types'
import {
    GetNuggetResponse,
    GetProductTypesWithBODResponse,
    GetProjectElementNodesResponse,
    GetProjectManufacturerProductListingResponse,
    GetProjectNuggetResponseForImportedNugget,
    ProductTypeWithBODItem,
    ProjectElementNodeWithMetadata,
} from 'api/graphQL/types/projectElement.types'
import { processEnv } from 'components/configuration/environmentVariables'
import debounce from 'lodash.debounce'
import isEqual from 'lodash.isequal'
import { AllProjectResponse, FacetItem } from 'Models/project'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { FixedSizeGrid } from 'react-window'
import { ProductListings } from 'shared/browserTitle'
import {
    combineAndSortManufacturers,
    selectUnselectAllManufacturers,
    toggleManufacturerSelection,
} from 'shared/utils/manufacturerListUtils'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    getDialogUrl,
    getFromLocalStorage,
    getProductTypeSelectionsData,
    removeDuplicateProductTypeFacets,
    replaceSpecAgentIdForProductTypes,
    unescapeElementDelimiter,
    updatePersistentConfigs,
    WORKSPACE_ENABLED_PTP_OPTIONS,
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY,
    WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY,
    WORKSPACE_PRODUCT_LISTING_ACTION,
    WORKSPACE_PRODUCT_LISTING_KEY,
    WORKSPACE_TREE_ELEMENT_NODES,
    WorkspaceAddSelectedProductCount,
    WorkspaceManufacturerContentItems,
    WorkspaceManufacturerListingItem,
    WorkspaceManufacturerSelected,
    WorkspaceProductListing,
    WorkspaceTreeElementNodes,
} from 'shared/utils/workspaceUtils'
import AdvancedFilters from './AdvancedFilters'
import { DialogContent } from './DialogContent'
import { DialogProvider } from './DialogContext'
import { DialogFilterProvider } from './DialogFilterContext'
import { RelatedProductsBanner } from './Footer/RelatedProductsBanner'
import { DialogAppBar } from './Header/DialogAppBar'
import { DialogHeader } from './Header/DialogHeader'
import { AddProprietaryListingDetailInput, DisplayTextFormatType, PartnershipType, ProductListingDialogFacet, ProductListingSelected, productOptions, ProductSearchResponseMinimal, SelectionType } from './ProductListingDialog.types'
import { checkPPLErrors, isProductTypeInProject, resolveAddListingToProjectParams } from './ProductListingDialog.utils'
import RemoveProductWarning from './Prompts/RemoveProductWarning'
import { SlideContent } from './SlideContent'
import { useProductListingDialogStore } from './store'
import { DialogTabs } from './Tabs/DialogTabs'
import { UnmappedProductDialogWrapper } from './UnmappedProductDialogWrapper'

export const PER_PAGE = 100
export const OPTIMISTIC_CARDS = 5

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex?.drawer + 1,
        color: theme.palette?.common.white,
    },
}))

const areBadgelessCardsEnabled =
    processEnv.REACT_APP_EnableBadgelessCards === '1'

const defaultListingOrder = `type${areBadgelessCardsEnabled ? ' desc' : ''
    },firmName,productType,productName,lastUpdated desc`

const defaultProductTypeSelections = (
    hasFamily: boolean,
    family: string,
    hasProductType: boolean,
    productType: string,
    ptpOptions?: ProductTypePropertiesInputType[]
) => {
    if (hasFamily && hasProductType) {
        return [
            {
                sectionName: family,
                productTypeName: productType,
                productTypeProperties: ptpOptions?.map((ptpOption) => ({
                    propertyName: ptpOption.propertyName.toString(),
                    propertyOptionIds: ptpOption.propertyOptionIds.map(
                        (property) => property.toString().normalize('NFKC')
                    ),
                })),
            },
        ]
    }
    return []
}

export const defaultProduct = {
    [productOptions.ADDED_TO_PROJECT]: false,
    [productOptions.FLAGGED_FOR_REVIEW]: false,
}

const sustainabilityResourcesOptions = ['Sustainable Minds']
const bimResourcesOptions = ['BIMsmith']
const defaultFilter: string[] = !areBadgelessCardsEnabled
    ? ['Premium MasterSpec', 'Premium']
    : ['Product Listing']

const ProductListingDialog: FC = () => {
    const { setHeaderVisibility, updateBrowserTitle } = React.useContext(
        AppBarContext
    )
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const setProductListings = useProductListingDialogStore(state => state.setProductListings)
    const productListings = useProductListingDialogStore(state => state.productListings)
    const checkStaleListings = useProductListingDialogStore(state => state.checkStaleListings)

    const { selectedProjectNode } = React.useContext(AreaContext)

    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const serviceWorker = navigator.serviceWorker

    const [closeSlide, setCloseSlide] = React.useState<boolean>(false)

    const history = useHistory()
    const location = useLocation()
    const classes = useStyles()

    const userDetails = useReactiveVar(userInfo)

    const [productsPage, setProductsPage] = React.useState<boolean>(false)

    const [isAEAllProductsToggled, setIsAEAllProductsToggled] = React.useState<
        boolean
    >(false)

    const { projectId } = useParams<{ projectId?: string }>()

    const currentUserInfoResponse = useContext(AuthContext) as IAuthContext

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const userId = React.useMemo(
        () => currentUserInfoResponse?.currentUserInfo.kpUserId,
        [currentUserInfoResponse]
    )

    const [searchLoading, setSearchLoading] = React.useState<boolean>(false)
    const [searchFetchMoreLoading, setSearchFetchMoreLoading] = React.useState<
        boolean
    >(false)
    const [currentTotal, setCurrentTotal] = React.useState<number>(PER_PAGE)
    const [currentRTotal, setCurrentRTotal] = React.useState<number>(PER_PAGE)
    const [refetch, setRefetch] = React.useState<boolean>(false)
    const [hasNextPage, setHasNextPage] = React.useState<boolean>(true)
    const [rHasNextPage, setRHasNextPage] = React.useState<boolean>(true)
    const [scrollState, setScrollState] = React.useState({
        rowIndex: 0,
        columnIndex: 0,
    })
    const [columnCount, setColumnCount] = React.useState<number>(PER_PAGE)
    const hasMountedRef = React.useRef(false)
    // Guards the [doRefetch, searchCalled] fallback effect from re-firing every time
    // doRefetch recreates due to manufacturerProductSearchInput spreading a new reference.
    // When all items are in Zustand cache, searchCalled stays false permanently —
    // without this guard the effect would loop indefinitely.
    const initialFetchFiredRef = React.useRef(false)
    const infiniteLoaderRef = React.useRef(null)
    // Track dialog-initiated additions to send service worker message after mutation (only used when beta toggle is enabled)
    const dialogAdditionDataRef = React.useRef<WorkspaceManufacturerSelected | null>(null)
    // Store search inputs for two-phase fetch (Phase 1 -> Phase 2 handoff)
    const currentSearchInputRef = React.useRef<Partial<UpdatedListingSearchInput> | null>(null)
    const currentRelatedSearchInputRef = React.useRef<Partial<UpdatedListingSearchInput> | null>(null)
    // Track last fetched search params for each tab to avoid unnecessary refetches
    const lastSearchParamsRef = React.useRef<Partial<UpdatedListingSearchInput> | null>(null)
    const lastRelatedParamsRef = React.useRef<Partial<UpdatedListingSearchInput> | null>(null)
    // Guard: Apollo 3.3.x re-broadcasts onCompleted after fetchMore cache updates.
    // Reset to false on each doRefetch so getMultiplePTsWithBOD fires only once per search.
    const bodCalledForCurrentSearchRef = React.useRef(false)
    const [reScroll, setReScroll] = React.useState<boolean>(false)

    const queryParams = new URLSearchParams(useLocation().search)
    const undecodedCategory = queryParams.get('category')
    const elementNode = queryParams.get('elementNode')
    const isInProject = queryParams.get('isInProject') === 'true'
    const category =
        undecodedCategory &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedCategory).split('||').join(' ')
        )
    const undecodedFamily = queryParams.get('family')
    const divisionNumber = queryParams.get('divisionNumber')
    const isForDivisionView =
        queryParams.get('selectedFilterType') === 'DIVISIONS'
    const family =
        undecodedFamily &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedFamily).split('||').join(' ')
        )
    const previousFamily = usePrevious(family)
    const undecodedProductType = queryParams.get('productType')
    const productType =
        undecodedProductType &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedProductType).split('||').join(' ')
        )

    const previousProductType = usePrevious(productType)
    const parentNodeId = queryParams.get('parentNodeId')
    const undecodedAssemblyPath = queryParams.get('assemblyPath')
    const assemblyPath =
        undecodedAssemblyPath &&
        decodeURIComponent(
            unescapeElementDelimiter(undecodedAssemblyPath)
                .split('#')
                .join(' ')
                .split('||')
                .join(' / ')
                .split('|')
                .join(' ')
        )

    const previousAssemblyPath = usePrevious(assemblyPath)
    const specAgentIdPt = queryParams.get('specAgentIdPt')
    const nodeId = queryParams.get('nodeId')
    const previousNodeId = usePrevious(nodeId)
    const elementId = queryParams.get('elementId')
    const elementType = queryParams.get('elementType')
    const isLastSubAssembly =
        queryParams.get('isLastSubAssembly') === 'true' ? true : false
    const isFromRevit = queryParams.get('source') === 'plugin' ? true : false

    const isAssembly = elementType === 'ASSEMBLY' && !isLastSubAssembly
    const isProductType = elementType === 'PRODUCTTYPE'
    const isProductFamily = elementType === 'PRODUCTFAMILY'
    const hasFamily = !!elementType && (isProductType || isProductFamily)

    const hasProductType = !!elementType && isProductType

    const isGettingSelectedNuggetRequired =
        isProductType && projectId && elementId

    const listingId = queryParams.get('listingId')

    const categoryNodeId = queryParams.get('categoryNodeId')
    const baseElementId = queryParams.get('baseElementId')
    const treePath = queryParams.get('treePath')
        ? decodeURIComponent(queryParams.get('treePath')!)
        : undefined

    // Determine if this is a division view based on tree path
    const isDivisionViewFromTreePath = React.useMemo(() => {
        if (!treePath) return false

        // Split the tree path by || to get path segments
        const pathSegments = treePath.split('||')
        if (pathSegments.length === 0) return false

        // Get the first segment and split by | to get the identifier
        const firstSegment = pathSegments[0]
        const firstSegmentParts = firstSegment.split('|')
        if (firstSegmentParts.length === 0) return false

        const firstIdentifier = firstSegmentParts[0].trim()
        // Division paths start with 2-digit division numbers (e.g., "03")
        // Assembly paths start with "A" followed by numbers (e.g., "A1020")
        const isDivisionFormat = /^\d{2}$/.test(firstIdentifier)
        const isAssemblyFormat = /^A\d+/.test(firstIdentifier)

        // Return true only if it matches division format and NOT assembly format
        return isDivisionFormat && !isAssemblyFormat
    }, [treePath])
    // Filters
    const [listingType, setListingType] = React.useState<string[]>([])
    const [product, setProduct] = React.useState<Record<string, boolean>>(
        defaultProduct
    )
    const [productSelected, setProductSelected] = React.useState<
        ProductListingSelected[]
    >([])
    const [
        productListingFlaggedForReview,
        setProductListingFlaggedForReview,
    ] = React.useState<ProductListingFlagForReview[]>([])
    const [specialty, setSpecialty] = useState<string[]>([])
    const [bimResources, setBIMResources] = React.useState<string[]>([])
    const [sustainabiliyResources, setSustainabilityResources] = React.useState<
        string[]
    >([])

    const [isRemoveWarningOpen, setIsRemoveWarningOpen] = React.useState<
        boolean
    >(false)

    const [listingInfoToDelete, setListingInfoToDelete] = React.useState<
        { listingId: string; isBOD: boolean } | undefined
    >()

    const defaultManufacturerProductSearchInput = React.useMemo(
        () => ({
            skip: 0,
            top: PER_PAGE,
            isOnAE: true,
            projectId: projectId,
            logEventSource: LogEventSource.AeViewProducts,
            productTypeSelections: [], // this field cannot be null. If it is null, the api will throw error
        }),
        [projectId]
    )

    const [
        manufacturerProductSearchInput,
        setManufacturerProductSearchInput,
    ] = React.useState<Partial<UpdatedListingSearchInput>>(
        defaultManufacturerProductSearchInput
    )

    const [searchFacets, setSearchFacets] = React.useState<
        Partial<SearchFacet>[]
    >([])

    const [productTypesWithBOD, setProductTypesWithBOD] = React.useState<
        ProductTypeWithBODItem[]
    >([])

    const [projectPTNames, setProjectPTNames] = React.useState<string[]>([])

    const [autosuggestFacet, setAutosuggestFacet] = React.useState<{
        name?: ProductListingDialogFacet
        value?: string
    }>({})

    const [autosuggestSaved, setAutosuggestSaved] = React.useState<boolean>(
        true
    )
    const [autosuggestCardSaved, setAutosuggestCardSaved] = React.useState<
        boolean
    >(true)
    const [
        isProductTypeFacetsLoading,
        setIsProductTypeFacetsLoading,
    ] = React.useState<boolean>(
        location.pathname.includes('products') ? false : !isAssembly
    )

    const [isAddElementsLoading, setIsAddElementsLoading] = React.useState<
        boolean
    >(false)

    const [
        isFlagForReviewInProgress,
        setIsFlagForReviewInProgress,
    ] = React.useState<boolean>(false)

    const [isBannerExpanded, setIsBannerExpanded] = React.useState<boolean>(
        false
    )

    const productSelectedListingIds = React.useMemo(
        () => productSelected.map((each) => each.listingId as string) ?? [],
        [productSelected]
    )

    const flaggedForReviewListingIds = React.useMemo(
        () =>
            productListingFlaggedForReview.map(
                (each) => each.listingId as string
            ) ?? [],
        [productListingFlaggedForReview]
    )

    const calculateEmptyListings = React.useCallback(() => {
        const addedToProject = product[productOptions.ADDED_TO_PROJECT]
        const flaggedForReview = product[productOptions.FLAGGED_FOR_REVIEW]
        const noProductListingFlagged = !productListingFlaggedForReview.length
        const noAddedToProjectListingIds = !productSelectedListingIds.length

        let isEmpty = false

        if (addedToProject) {
            isEmpty =
                noAddedToProjectListingIds &&
                (!flaggedForReview ||
                    (!!flaggedForReview && noProductListingFlagged))
        } else if (flaggedForReview && noProductListingFlagged) {
            isEmpty = true
        }

        return isEmpty
    }, [product, productSelectedListingIds, productListingFlaggedForReview])

    const isEmptyListingDueToFilter = React.useMemo(() => {
        return calculateEmptyListings()
    }, [calculateEmptyListings])

    const defaultSearchFacetInputs = React.useCallback(() => {
        let facetInputs: SearchFacetInput[] = [
            { name: 'isActive', values: ['true'] },
        ]
        if (hasFamily) {
            facetInputs = [
                ...facetInputs,
                { name: 'SectionNames', values: [family as string] },
            ]
        }
        if (category && category !== 'All Categories') {
            facetInputs = [
                ...facetInputs,
                { name: 'categories', values: [category] },
            ]
        }
        if (
            !isProductFamily &&
            !isProductType &&
            (isAssembly || isLastSubAssembly)
        ) {
            facetInputs = [
                ...facetInputs,
                { name: 'assemblyNodeId', values: [nodeId as string] },
            ]
        }
        return facetInputs
    }, [
        category,
        hasFamily,
        family,
        isProductFamily,
        isProductType,
        isAssembly,
        isLastSubAssembly,
        nodeId,
    ])

    const [searchFacetInputs, setSearchFacetInputs] = React.useState<
        SearchFacetInput[]
    >(defaultSearchFacetInputs())

    const [productTypeSelections, setProductTypeSelections] = React.useState<
        Partial<ProductTypeSelectionInput>[]
    >()

    const defaultProductTypeFacets = useMemo(() => {
        return [
            {
                value: productType ?? '',
                specAgentProductTypeId: specAgentIdPt,
                parentProductFamily: family!,
                // At family level there is no PT ProjectNugget yet — using the family's
                // elementId here causes it to be passed as a PT nugget ID to the mutation,
                // which is the wrong entity type and triggers an Object Reference error on BE.
                pTElementId: isProductFamily ? null : elementId,
                pTNodeId: isProductFamily ? null : nodeId,
                familyElementId: elementId,
                familyNodeId: nodeId,
            },
        ]
    }, [productType, specAgentIdPt, family, elementId, nodeId, isProductFamily])

    const [productTypeFacets, setProductTypeFacets] = React.useState<
        Partial<SearchFacets>[]
    >(defaultProductTypeFacets)

    const [propertyOptions, setPropertyOptions] = React.useState<
        ProductTypePropertiesInputType[]
    >([])
    const [orderBy, setOrderBy] = React.useState<string>()
    const [dialogTab, setDialogTab] = React.useState(0)
    const previousDialogTab = usePrevious(dialogTab)
    // Track which tabs have been fetched to avoid unnecessary refetches
    const [tabsFetched, setTabsFetched] = React.useState({ search: false, related: false })
    const productTypeWithBODForDivisionInput = React.useMemo(
        () => ({
            projectId,
            productFamilyGroupId: nodeId,
        }),
        [projectId, nodeId]
    )
    const isDivisionSelected = React.useMemo(
        () => !!divisionNumber && !!isForDivisionView,
        [divisionNumber, isForDivisionView]
    )

    const [
        projectProductListingCount,
        setProjectProductListingCount,
    ] = React.useState<number | undefined>()

    const [selectedManufacturers, setSelectedManufacturers] = React.useState<
        SelectedManufacturerListing[]
    >([])

    const handleMultiSelectManufacturer = React.useCallback(
        (listingId: string, listingLevel: ListingLevel, firmName: string, productType?: string, specAgentProductTypeId?: number) => {
            setSelectedManufacturers((current) =>
                toggleManufacturerSelection(
                    listingId,
                    listingLevel,
                    firmName,
                    current,
                    productSelectedListingIds,
                    productType,
                    specAgentProductTypeId
                )
            )
        },
        [productSelectedListingIds]
    )

    // Reusable callback to send service worker message with product count
    const sendServiceWorkerMessage = React.useCallback(
        async (selectedProductsCount: number) => {
            const message = {
                [WORKSPACE_PRODUCT_LISTING_ACTION]: {
                    selectedProducts: selectedProductsCount,
                } as WorkspaceAddSelectedProductCount,
            }

            if (serviceWorker?.controller) {
                serviceWorker.controller.postMessage(message)
            } else if (serviceWorker) {
                try {
                    const registration = await serviceWorker.ready
                    if (registration.active) {
                        registration.active.postMessage(message)
                    }
                } catch (error) {
                    console.warn(
                        'Service worker not available for message passing:',
                        error
                    )
                }
            } else {
                console.warn('Service worker not available')
            }
        },
        [serviceWorker]
    )

    const [getSelectedProducts] = useLazyQuery<
        GetProjectManufacturerProductListingResponse
    >(GetProjectManufacturerProductListingQuery, {
        displayName: 'ProjectProductListing',
        fetchPolicy: 'no-cache',
        variables: {
            input: {
                projectId,
                projectElementType: ProjectElementType.PRODUCTTYPE, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
            } as GetProjectManufacturerProductListingInput,
        },
        onCompleted: (data) => {
            const projectManufacturerProductListing =
                data?.getProjectManufacturerProductListing || []

            setProductSelected(
                projectManufacturerProductListing.map((each) => ({
                    listingId: each.listingId as string,
                    selectionType: each.isBOD ? 'BOD' : 'Product',
                    isBOD: each.isBOD ?? false,
                    isActive: each.isBOD ?? false,
                    type: each.type as ListingLevel,
                    firmName: each.firmName as string,
                    hasSupportedManufacturersOptions:
                        each.hasSupportedManufacturersOptions ?? false,
                    productType: each.productType,
                    elementId: each.elementId as string,
                })) ?? []
            )
            setProjectProductListingCount(
                projectManufacturerProductListing.length
            )

            getProductFlaggedForReviewProducts({
                variables: {
                    projectId: projectId,
                },
            })

            // Send service worker message with updated count
            sendServiceWorkerMessage(projectManufacturerProductListing.length)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on fetching project product listing',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectProductListingError`,
                },
            })
        },
    })

    // Phase 1: Lightweight query for listingId + lastUpdated only
    const [
        getListingsMinimal,
        { data: minimalData, loading: minimalLoading, fetchMore: minimalFetchMore }
    ] = useLazyQuery<ProductSearchResponseMinimal>(ProductSearchQueryMinimal, {
        displayName: 'ProductSearchMinimal',
        fetchPolicy: 'network-only', // network-only prevents cache-and-network from re-fetching skip=0 after fetchMore, which would overwrite accumulated pagination data
        onCompleted: (data) => {
            // Facets are search-level metadata — handle them here in Phase 1, not Phase 2.
            // Apollo 3.3.x re-fires onCompleted after fetchMore cache updates, so guard
            // getMultiplePTsWithBOD with a ref to ensure it runs only once per doRefetch.
            if (data?.listingSearch?.facets) {
                if (!productsPage && !isAEAllProductsToggled) {
                    const newSearchFacets = data.listingSearch.facets.filter(
                        (item) =>
                            item.name !== 'SectionNames' &&
                            item.name !== 'ProductType' &&
                            item.name !== 'FirmName'
                    )
                    // Functional update reads the current state (not a stale closure), so
                    // autosuggest-provided SectionNames/ProductType/FirmName entries are preserved
                    // even if they were added/removed after this query was initiated.
                    setSearchFacets((currentFacets) => [
                        ...newSearchFacets,
                        ...currentFacets.filter(
                            (item) =>
                                item.name === 'SectionNames' ||
                                item.name === 'ProductType' ||
                                item.name === 'FirmName'
                        ),
                    ])
                } else {
                    setSearchFacets(data.listingSearch.facets)
                }
                if (!bodCalledForCurrentSearchRef.current) {
                    bodCalledForCurrentSearchRef.current = true
                    getMultiplePTsWithBOD()
                }
            }

            // Phase 2: Check cache and fetch full data if needed
            if (!data?.listingSearch?.matches) {
                setSearchLoading(false)
                setIsAddElementsLoading(false)
                return
            }

            const minimalListings = data.listingSearch.matches
            const listingsToFetch = checkStaleListings(minimalListings)

            if (listingsToFetch.length === 0) {
                // All data is fresh in cache, no need for Phase 2.
                setSearchLoading(false)
                setIsAddElementsLoading(false)
                return
            }

            // Only trigger Phase 2 if this is the active tab
            if (currentSearchInputRef.current && dialogTab === 0) {
                getListings({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...currentSearchInputRef.current,
                            listingIdsList: listingsToFetch,
                        },
                    },
                })
            } else if (dialogTab !== 0) {
                // Tab switched before Phase 2, just clear loading
                setSearchLoading(false)
                setIsAddElementsLoading(false)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingsPage_ProductSearchError`,
                },
            })
            setSearchLoading(false)
            setIsAddElementsLoading(false)
        },
    })

    const [
        getListings,
        {
            data: searchData,
            fetchMore: searchFetchMore,
            called: searchCalled,
            loading: getListingsLoading,
        },
    ] = useLazyQuery<ProductSearchResponse>(ProductSearchQuery, {
        displayName: 'ProductSearchResult',
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        onCompleted: (data) => {
            setProductListings(data?.listingSearch?.matches || [])
            setSearchLoading(false)
            setSearchFetchMoreLoading(false)
            setIsAddElementsLoading(false)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingsPage_ProductSearchError`,
                },
            })
            setSearchLoading(false)
            setSearchFetchMoreLoading(false)
            setIsAddElementsLoading(false)
        },
    })

    // Phase 1 for related products: Lightweight query
    const [
        getRelatedProductsMinimal,
        { data: relatedMinimalData, loading: relatedMinimalLoading, fetchMore: relatedMinimalFetchMore }
    ] = useLazyQuery<ProductSearchResponseMinimal>(ProductSearchQueryMinimal, {
        displayName: 'RelatedProductsMinimal',
        fetchPolicy: 'network-only', // network-only prevents cache-and-network from re-fetching skip=0 after fetchMore, which would overwrite accumulated pagination data
        onCompleted: (data) => {
            // Phase 2: Check cache and fetch full data if needed
            // Only process if this is the active tab (dialogTab === 1)
            if (dialogTab !== 1) {
                // Fetched for count only while on tab 0, don't do anything else
                return
            }

            if (!data?.listingSearch?.matches) {
                setSearchLoading(false)
                return
            }

            const listingsToFetch = checkStaleListings(data.listingSearch.matches)

            if (listingsToFetch.length === 0) {
                // All data is fresh in cache, no need for Phase 2.
                setSearchLoading(false)
                return
            }

            // Get the current search input from the ref (set before Phase 1)
            // Use listingIdsList to fetch ONLY stale items (cache optimization)
            if (currentRelatedSearchInputRef.current) {
                getRelatedProducts({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...currentRelatedSearchInputRef.current,
                            listingIdsList: listingsToFetch,
                        },
                    },
                })
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error retrieving remaining products',
                    variant: 'error',
                    id: `ProductListingsPage_RelatedProductsError`,
                },
            })
            setSearchLoading(false)
        },
    })

    const [
        getRelatedProducts,
        {
            data: relatedProducts,
            called: relatedProductsCalled,
            fetchMore: getMoreRelatedProducts,
            loading: getrelatedProductsLoading,
        },
    ] = useLazyQuery<ProductSearchResponse>(ProductSearchQuery, {
        displayName: 'RemainingProductsQuery',
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        onCompleted: (data) => {
            setProductListings(data?.listingSearch?.matches || [])
            setSearchLoading(false)
            setSearchFetchMoreLoading(false)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error retrieving remaining products',
                    variant: 'error',
                    id: `ProductListingsPage_RelatedProductsError`,
                },
            })
            setSearchLoading(false)
            setSearchFetchMoreLoading(false)
        },
    })

    const [
        getListingsAutosuggest,
        { loading: autosuggestLoading, data: autosuggestData },
    ] = useLazyQuery<ProductSearchAutosuggestResponse>(
        ProductSearchAutosuggestQuery,
        {
            displayName: 'ProductSearchAutoSuggestResult',
            fetchPolicy: 'no-cache',
            variables: {
                input: {
                    projectId,
                    projectElementType: ProjectElementType.PRODUCTTYPE, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
                } as GetProjectManufacturerProductListingInput,
            },
            onCompleted: () => {
                setAutosuggestSaved(false)
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on product autosuggest in dialog',
                        variant: 'error',
                        id: `ProductListingsDialog_ProductAutosuggestError`,
                    },
                })
            },
        }
    )
    const [
        getListingCardAutosuggest,
        { loading: autosuggestCardLoading, data: autosuggestCardData },
    ] = useLazyQuery<ProductCardAutosuggestResponse>(
        ProductCardAutosuggestQuery,
        {
            displayName: 'ProductCardAutoSuggestResult',
            fetchPolicy: 'no-cache',
            onCompleted: () => {
                setAutosuggestCardSaved(false)
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on product autosuggest in dialog',
                        variant: 'error',
                        id: `ProductListingsDialog_ProductAutosuggestError`,
                    },
                })
            },
        }
    )
    const [
        getProject,
        { data: projectData, loading: projectLoading, called: projectCalled },
    ] = useLazyQuery<AllProjectResponse>(GetProjectQuery, {
        displayName: 'ProjectDetail',
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'ProductListingsPage_ProjectDetailError',
                },
            })
        },
    })

    const {
        data: projectProductListingData,
        loading: projectProductListingLoading,
    } = useQuery<GetProjectManufacturerProductListingResponse>(
        GetProjectManufacturerProductListingQuery,
        {
            displayName: 'ProjectProductListing',
            fetchPolicy: 'no-cache',
            variables: {
                input: {
                    projectId,
                    projectElementType: isProductType
                        ? ProjectElementType.PRODUCTTYPE
                        : isProductFamily
                            ? ProjectElementType.PRODUCTFAMILY
                            : ProjectElementType.ASSEMBLY, // can be PRODUCTFAMILY, ASSEMBLIES, etc\
                } as GetProjectManufacturerProductListingInput,
            },
            skip: !projectId,
            onCompleted: () => {
                getProductFlaggedForReviewProducts({
                    variables: {
                        projectId: projectId,
                    },
                })
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on fetching project product listing',
                        variant: 'error',
                        id: `ProductListingsPage_ProjectProductListingError`,
                    },
                })
            },
        }
    )

    const wasListingsLoading = usePrevious(projectProductListingLoading)

    // Ensures atleast one variable exist before calling getProductTypesWithBod
    const onGetProductTypesWithBod = React.useCallback(
        (nodeIds: string[] | undefined) => {
            if (isDivisionSelected && !!productTypeWithBODForDivisionInput) {
                // From Division view with valid variables
                getProductTypesWithBod({
                    variables: {
                        nodeIds: undefined,
                        input: productTypeWithBODForDivisionInput,
                    },
                })
            } else if (
                !isDivisionSelected &&
                !!nodeIds?.length &&
                nodeIds[0] !== 'undefined'
            ) {
                // From Assembly view with valid variables
                getProductTypesWithBod({
                    variables: {
                        nodeIds: nodeIds,
                        input: undefined,
                    },
                })
            }
        },
        [isDivisionSelected, productTypeWithBODForDivisionInput]
    )

    const [getProductTypesWithBod] = useLazyQuery<
        GetProductTypesWithBODResponse
    >(GetProductTypesWithBODQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'getProductTypesWithBOD',
        onCompleted: (data) => {
            if (data) {
                const bodItems = data.getProductTypesWithBod as ProductTypeWithBODItem[]
                setProductTypesWithBOD(bodItems)

                // Backfill specAgentProductTypeId from BOD into facets when URL lacks specAgentIdPt
                if (!specAgentIdPt && bodItems?.length) {
                    const bodByName = new Map(
                        bodItems
                            .filter((b) => b.specAgentProductTypeId)
                            .map((b) => [b.productTypeName, b.specAgentProductTypeId])
                    )
                    const needsBackfill = productTypeFacets.some(
                        (f) => !f.specAgentProductTypeId && bodByName.has(f.value ?? '')
                    )

                    if (needsBackfill) {
                        setProductTypeFacets((prev) =>
                            prev.map((facet) => {
                                const id = bodByName.get(facet.value ?? '')
                                return !facet.specAgentProductTypeId && id
                                    ? { ...facet, specAgentProductTypeId: id }
                                    : facet
                            })
                        )
                        setTabsFetched({ search: false, related: false })
                        setRefetch(true)
                    }
                }
            }
        },
        onError: (err) => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on fetching product types with BOD',
                    variant: 'error',
                    id: `ProductListingsPage_ProjectTypeWithBODError`,
                },
            })
        },
    })

    const [
        getSelectedNugget,
        { data: projectNuggetData, loading: projectNUggetLoading },
    ] = useLazyQuery<GetProjectNuggetResponseForImportedNugget>(
        GetProjectNuggetForProductListingsQuery(6),
        {
            fetchPolicy: 'no-cache',
            displayName: 'GetProjectNugget',
            onCompleted: (data) => {
                if (!data) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Product Type for the selected Product Listing does not exist',
                            variant: 'error',
                            id: `ProductListingsPage_ProjectNuggetError`,
                        },
                    })
                }
            },
            onError: () => {
                // removed error message because, the elementId could be a baseElementId
                // let's try to query for the base nugget
                getNugget({
                    variables: {
                        id: isProductType ? elementId : '',
                        nodeId: nodeId,
                    },
                })
            },
        }
    )

    const [getNugget] = useLazyQuery<GetNuggetResponse>(
        GetNuggetForProductListingsQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'Nugget',
            onCompleted: (data) => {
                if (!data) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Product Type for the selected Product Listing does not exist',
                            variant: 'error',
                            id: `ProductListingsPage_ProjectNuggetError`,
                        },
                    })
                }
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error on fetching nugget',
                        variant: 'error',
                        id: `ProductListingsPage_ProjectNuggetError`,
                    },
                })
            },
        }
    )

    // Fetch product listing flagged for review
    const [getProductFlaggedForReviewProducts] = useLazyQuery<
        GetFlagForReviewResponse
    >(GetProductFlagForReviewQuery, {
        displayName: 'ProductListingFlagForReview',
        fetchPolicy: 'no-cache',
        variables: {
            projectId: projectId,
        },
        onCompleted: (data: GetFlagForReviewResponse) => {
            setProductListingFlaggedForReview(
                data?.getProductListingsFlaggedForReview?.items || []
            )
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message:
                        'Error on fetching product listing flagged for review',
                    variant: 'error',
                    id: `ProductListingsPage_ProductListingFlaggedForReviewError`,
                },
            })
        },
    })

    const [addListingElements] = useMutation<{
        addManufacturerProductListingElements: AddManufacturerProductListingElementResult
        updatedInput: Partial<CreateProjectManufacturerProductListingInput>
        projectElementNodes: Partial<ProjectElementNode[]>
        createProjectManufacturerProductListingInput: Partial<
            CreateProjectManufacturerProductListingInput
        >
    }>(AddManufacturerProductListingElementsMutation, {
        onCompleted: (data) => {
            if (data?.addManufacturerProductListingElements.updatedInput) {
                const projectElementNodes =
                    data?.addManufacturerProductListingElements
                        .projectElementNodes ?? []
                // call add listing mutation
                const {
                    listingId,
                    projectId,
                    __typename,
                    ...input
                }: any = data?.addManufacturerProductListingElements?.updatedInput

                if (
                    isBetaWorkspaceToggleEnabled &&
                    dialogAdditionDataRef.current
                ) {
                    const messageData = dialogAdditionDataRef.current
                    const updatedInput =
                        data?.addManufacturerProductListingElements
                            ?.updatedInput

                    // projectElementNodes lists ancestors first (ASSEMBLY → FAMILY → new PT
                    // + siblings); [0] is the top-level assembly, not the new PT. The new PT
                    // is the node whose elementId matches updatedInput.elementId (the server-
                    // assigned project element id of the newly created PT). Using the wrong
                    // node id in the SW message causes the receiver to fire
                    // getProjectNuggetContentView with a PT elementId + assembly nodeId, which
                    // the backend can't resolve ("DocumentTemplateView object is null") — pill
                    // never appears.
                    const newPTNode = projectElementNodes?.find(
                        (n) => n?.elementId === input?.elementId
                    )

                    // Send the message with all necessary data
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                            ...messageData,
                            elementId: input?.elementId, // Use the elementId from updatedInput
                            nodeId: newPTNode?.id || messageData.nodeId,
                            firmName:
                                updatedInput?.firmName || messageData.firmName,
                            productName: updatedInput?.productName,
                            isFromDialogMutation: true,
                        } as WorkspaceManufacturerSelected,
                        // Include the full mutation response data for useServiceWorkerListener
                        addManufacturerProductListingElements:
                            data?.addManufacturerProductListingElements,
                    })

                    // Update workspace element tree with projectElementNodes
                    if (projectElementNodes && projectElementNodes.length > 0) {
                        updateWorkspaceElementTree(
                            projectElementNodes,
                            input?.elementId
                        )
                    }
                }

                // Remove flag for review if needed
                if (!!listingId && !!projectId) {
                    const hasFlagForReview = !!productListingFlaggedForReview.find(
                        (item) => item.listingId === listingId
                    )
                    if (hasFlagForReview) {
                        setIsFlagForReviewInProgress(true)
                        removeFlagForReview({
                            variables: {
                                projectId: projectId,
                                listingId: listingId,
                            },
                        }).then(() => {
                            setManufacturerProductSearchInput((past) => ({
                                ...past,
                                projectId,
                                listingIdsList: past.listingIdsList?.filter(
                                    (each) => each !== listingId
                                ),
                            }))
                            setIsFlagForReviewInProgress(false)
                        })
                    }
                }

                if (input.isBOD) {
                    addListingAsBOD({
                        variables: {
                            input: {
                                ...input,
                                listingId,
                                projectId,
                                isFromRevit,
                            },
                        },
                    }).then((value) => {
                        if (
                            isBetaWorkspaceToggleEnabled &&
                            dialogAdditionDataRef.current
                        ) {
                            dialogAdditionDataRef.current = null
                        }

                        if (
                            isProductType ||
                            isProductFamily ||
                            searchFacetInputs?.filter(
                                (item) => item?.name === 'ProductType'
                            )?.[0]?.values?.length!! !== 0
                        ) {
                            updateWorkspaceItems(
                                value?.data
                                    ?.setProjectManufacturerProductListingAsBOD ??
                                [],
                                { ...input, listingId, projectId },
                                projectElementNodes
                            )
                        }
                        // For beta workspace, don't update workspace element tree
                        // as it's handled differently through the service worker
                        if (!isBetaWorkspaceToggleEnabled) {
                            // Send WORKSPACE_TREE_ELEMENT_NODES even when projectElementNodes is empty:
                            // empty = PT already exists in project (no new nodes created), but WorkspacePage
                            // still needs to receive the message to trigger count refreshes via its setTimeout.
                            if (projectElementNodes) {
                                updateWorkspaceElementTree!(
                                    projectElementNodes,
                                    input?.elementId
                                )
                            }
                        }
                        updateProductTypeFacets('ProductType')
                        setIsAddElementsLoading(false)
                    })
                } else {
                    addListing({
                        variables: {
                            input: {
                                ...input,
                                listingId,
                                projectId,
                                isFromRevit,
                            },
                        },
                    }).then((value) => {
                        if (
                            isBetaWorkspaceToggleEnabled &&
                            dialogAdditionDataRef.current
                        ) {
                            dialogAdditionDataRef.current = null
                        }

                        const productTypeFilterLength =
                            searchFacetInputs?.filter(
                                (item) => item?.name === 'ProductType'
                            )?.[0]?.values?.length || 0

                        if (isProductType || isProductFamily || productTypeFilterLength !== 0) {
                            // update workspace items
                            updateWorkspaceItems(
                                value?.data
                                    ?.addManufacturerProductListingInProject ??
                                [],
                                data?.addManufacturerProductListingElements
                                    ?.updatedInput
                            )
                        }
                        // For beta workspace, don't update workspace element tree
                        // as it's handled differently through the service worker
                        if (!isBetaWorkspaceToggleEnabled) {
                            if (projectElementNodes) {
                                updateWorkspaceElementTree!(
                                    projectElementNodes,
                                    input?.elementId
                                )
                            }
                        }
                        updateProductTypeFacets('ProductType')
                        setIsAddElementsLoading(false)
                    })
                }
            }
        },
        onError: (error) => {
            setIsAddElementsLoading(false)
            setIsFlagForReviewInProgress(false)
            showGlobalSnackbar?.({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `ProductListingDialog-AddProductListingElements`,
                },
            })
        },
    })

    // Helper function to refresh selected products and send service worker message
    const refreshSelectedProducts = React.useCallback(() => {
        getSelectedProducts()
    }, [getSelectedProducts])

    const [addListing] = useMutation<
        { addManufacturerProductListingInProject: ContentItem[] },
        { input: Partial<CreateProjectManufacturerProductListingInput> }
    >(AddManufacturerProductListingInProjectMutation, {
        onCompleted: refreshSelectedProducts,
        onError: () => {
            refreshSelectedProducts() // Ensure count is refreshed even on error
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in adding the listing.`,
                    variant: 'error',
                    id:
                        'ProductListingsPage_AddManufacturerProductListingInProjectError',
                },
            })
        },
    })

    const [addListingAsBOD] = useMutation<
        { setProjectManufacturerProductListingAsBOD: ContentItem[] },
        { input: Partial<CreateProjectManufacturerProductListingInput> }
    >(SetProjectManufacturerProductListingAsBODMutation, {
        onCompleted: refreshSelectedProducts,
        onError: () => {
            refreshSelectedProducts() // Ensure count is refreshed even on error
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in adding the listing.`,
                    variant: 'error',
                    id:
                        'ProductListingsPage_AddManufacturerProductListingInProjectError',
                },
            })
        },
    })

    const [deleteListing] = useMutation<
        { deleteProjectManufacturerProductListing: ContentItem[] },
        { input: Partial<DeleteProjectManufacturerProductListingInput> }
    >(DeleteProjectManufacturerProductListingMutation, {
        onError: (err) => {
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem in deleting the listing.`,
                    variant: 'error',
                    id:
                        'ProductListingsPage_DeleteProjectManufacturerProductListingError',
                },
            })
        },
    })

    const [addFlagForReview] = useMutation<CreateFlagForReviewResponse>(
        CreateProductFlagForReviewMutation,
        {
            onError: (error) => {
                setIsFlagForReviewInProgress(false)
                showGlobalSnackbar?.({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: `ProductListingDialog-AddProductListingFlagForReview`,
                    },
                })
            },
        }
    )

    const [toggleMultipleManufacturerSelections] = useMutation(
        ToggleMultipleManufacturerSelectionsMutation,
        {
            onCompleted: (data) => {
                const manufacturersToProcess = [...selectedManufacturers]
                const { projectElementNodes, updatedElementId } =
                    data?.toggleMultipleManufacturerSelections || {}

                // Use updatedElementId if product type was just added, otherwise use current elementId
                const effectiveElementId = updatedElementId || (elementId as string)

                // Send postMessage for each selected manufacturer to update workspace nodes
                manufacturersToProcess.forEach((manufacturer) => {
                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                            listingId: manufacturer.listingId,
                            isSelected: true,
                            isFirstBOD: false, // Since this is for multiple selections, none are "first BOD"
                            listingText: manufacturer.firmName,
                            listingType: manufacturer.type,
                            firmName: manufacturer.firmName,
                            isBOD: false, // Multiple selections are typically for regular products, not BOD
                            hasSupportedManufacturersOptions: false, // Default value for multiple selections
                            elementId: effectiveElementId,
                            nodeId: nodeId,
                            isFromDialogMutation: true, // Flag to indicate this is from the multi-select mutation
                        } as WorkspaceManufacturerSelected,
                    })
                })

                // Update workspace element tree with projectElementNodes if product type was added
                if (projectElementNodes && projectElementNodes.length > 0) {
                    updateWorkspaceElementTree(
                        projectElementNodes,
                        effectiveElementId
                    )
                }

                setIsAddElementsLoading(false)
                setSelectedManufacturers([])
                refreshSelectedProducts()

                showGlobalSnackbar?.({
                    variables: {
                        message:
                            'Manufacturer selections updated successfully.',
                        variant: 'success',
                        id:
                            'ProductListingsDialog_ToggleMultipleManufacturerSelectionsSuccess',
                    },
                })
            },
            onError: (error) => {
                setIsAddElementsLoading(false)
                showGlobalSnackbar?.({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id:
                            'ProductListingsDialog_ToggleMultipleManufacturerSelectionsError',
                    },
                })
            },
        }
    )

    const handleToggleMultipleManufacturerSelections = React.useCallback(
        (isBOD: boolean = false, isRetainProperties: boolean = true) => {
            setIsAddElementsLoading(true)

            // Get productFamilyId from projectNuggetData, with facet fallback
            const nuggetProductFamilyId = projectNuggetData?.getProjectNugget
                .projectProductTypePropertiesView?.productFamilyId
            const {
                familyElementId: facetFamilyElementId,
            } = getProjectNuggetIdAndFamilyIdFromFacets(productTypeFacets)

            const projectNuggetId = projectNuggetData?.getProjectNugget.id
            const productFamilyId = nuggetProductFamilyId || facetFamilyElementId || ''

            // Combine newly selected manufacturers with already added manufacturers
            const allSlectedManufacturers = combineAndSortManufacturers(
                productSelected,
                selectedManufacturers,
                projectNuggetId,
                productFamilyId,
                nodeId || undefined
            )

            toggleMultipleManufacturerSelections({
                variables: {
                    input: {
                        projectId: projectId,
                        isBOD: isBOD,
                        isRetainProperties: isRetainProperties,
                        isProductFamilyGroupView: isDivisionViewFromTreePath,
                        selectedManufacturers: allSlectedManufacturers,
                    },
                },
            })
        },
        [
            projectId,
            projectNuggetData,
            selectedManufacturers,
            productSelected,
            toggleMultipleManufacturerSelections,
            productTypeFacets,
            nodeId,
            isDivisionViewFromTreePath,
        ]
    )


    const [removeFlagForReview] = useMutation<RemoveFlaggedForReviewResponse>(
        RemoveProductFlagForReviewMutation,
        {
            onError: (error) => {
                setIsFlagForReviewInProgress(false)
                showGlobalSnackbar?.({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: `ProductListingDialog-RemoveProductListingFlagForReview`,
                    },
                })
            },
        }
    )

    const getRelatedProductsFacets = React.useCallback(
        (facetInput: Partial<FacetItem>[]) => {
            let relatedProductsFacet = [
                ...facetInput,
                {
                    name: 'isForNonMasterspecFamily',
                    values: ['true'],
                },
                {
                    name: 'categories',
                    values: [category],
                },
                {
                    name: 'listingLevels',
                    values: [
                        ListingLevel.ProductListing,
                        ListingLevel.Product,
                        ListingLevel.None,
                        ListingLevel.PremiumProprietary,
                    ],
                },
            ]

            if (isOMUser) {
                relatedProductsFacet = [
                    ...relatedProductsFacet,
                    {
                        name: 'nodeIds',
                        values: [nodeId!],
                    },
                ]
            }

            return Array.from(
                new Set(
                    relatedProductsFacet
                        ?.filter(
                            (facet) =>
                                facet.name !== 'SectionNames' &&
                                facet.name !== 'specAgentProductTypeId'
                        )
                        .map((facet) => facet.name)
                )
            )
                ?.map((name) => {
                    if (name === 'categories') {
                        if (category !== 'All Categories') {
                            return {
                                ...relatedProductsFacet?.find(
                                    (facetName) => facetName?.name === name
                                ),
                            }
                        }
                    } else if (name !== 'assemblyNodeId') {
                        return {
                            ...relatedProductsFacet?.find(
                                (facetName) => facetName?.name === name
                            ),
                        }
                    }
                })
                ?.filter((x) => !!x)
        },
        [category, isOMUser]
    )

    const isItemLoaded = (index: number) => {
        const item = (dialogTab === 0 ? mergedProducts : mergedRelatedProducts)?.[index]
        if (!item) return false
        // Require full data in productListings — minimal placeholders (only listingId + lastUpdated)
        // must not count as loaded. This covers both the load-more window (Phase 2 pending) and
        // items Phase 2 never returns (e.g. server filters out inactive listings for listingIdsList queries).
        return !!productListings[item.listingId ?? '']
    }

    const setScrollRowAndColumn = React.useCallback(
        (rowIndex: number, columnIndex: number) => {
            setScrollState({ rowIndex, columnIndex })
        },
        []
    )

    const getSearchInput = React.useCallback(() => {
        const listingLevelFacet =
            manufacturerProductSearchInput.facets &&
            manufacturerProductSearchInput.facets.find(
                (facet) => facet.name === 'listingLevels'
            )?.values

        // Get family and product type filter count
        const familyFilterCount =
            manufacturerProductSearchInput.facets?.find(
                (facet) => facet.name === 'SectionNames'
            )?.values?.length || 0

        const ptFilterCount =
            manufacturerProductSearchInput.facets?.find(
                (facet) => facet.name === 'specAgentProductTypeId'
            )?.values?.length || 0

        // Set to assembly view if there are no family and product type filters
        const isChangeToAssemblyView =
            elementType !== 'ASSEMBLY' &&
            familyFilterCount + ptFilterCount === 0

        const isForDivisionViewFlag =
            isForDivisionView &&
            (elementType === 'ASSEMBLY' || isChangeToAssemblyView) &&
            !isAEAllProductsToggled

        let currentFacets = manufacturerProductSearchInput.facets || []

        if (!productsPage && !!manufacturerProductSearchInput.facets) {
            if (!!isAEAllProductsToggled) {
                // All products pill selected, remove assembly filter
                currentFacets = [
                    ...(currentFacets?.filter(
                        (facet) => facet.name !== 'assemblyNodeId'
                    ) || []),
                ]
            } else if (!isAssembly) {
                // If not in assembly view (division view)
                if (isChangeToAssemblyView && !!categoryNodeId) {
                    // No facet/filters for family and product type
                    // Replace assembly node with category node Id
                    currentFacets = [
                        ...(currentFacets?.filter(
                            (facet) => facet.name !== 'assemblyNodeId'
                        ) || []),
                    ]
                    currentFacets?.push({
                        name: 'assemblyNodeId',
                        values: [categoryNodeId],
                    })
                }
                // Remove SectionNames if empty as it causes issues
                if (familyFilterCount === 0) {
                    currentFacets = [
                        ...(currentFacets?.filter(
                            (facet) => facet.name !== 'SectionNames'
                        ) || []),
                    ]
                }
            }
        }

        if (specialty.includes('Manufacturer Options')) {
            currentFacets = [
                ...currentFacets.filter(
                    (past) => past.name !== 'withManufacturerOption'
                ),
                { name: 'withManufacturerOption', values: ['true'] },
            ]
        } else {
            currentFacets = currentFacets.filter(
                (facet) => facet.name !== 'withManufacturerOption'
            )
        }

        if (!productsPage && isOMUser) {
            currentFacets = [
                ...currentFacets,
                {
                    name: 'nodeIds',
                    values: [nodeId!],
                },
            ]
        }

        const searchInput: Partial<UpdatedListingSearchInput> =
            productsPage &&
                (!listingLevelFacet || listingLevelFacet.length === 0)
                ? {
                    ...manufacturerProductSearchInput,
                    facets: [
                        ...(manufacturerProductSearchInput.facets?.filter(
                            (facet) => facet.name !== 'listingLevels'
                        ) || []),
                        {
                            name: 'listingLevels',
                            values: defaultFilter,
                        },
                    ],
                }
                : {
                    ...manufacturerProductSearchInput,
                    isForDivisionView: isForDivisionViewFlag
                        ? true
                        : undefined,
                    facets: currentFacets,
                }
        return searchInput
    }, [
        manufacturerProductSearchInput,
        isForDivisionView,
        specialty,
        elementType,
        isAEAllProductsToggled,
        isAssembly,
        categoryNodeId,
        isOMUser,
        productsPage,
        nodeId,
    ])

    // Returns false if isForDivisionView is true and there are no valid facets
    const isDivisionFacetsValid = React.useCallback(
        (searchInput: Partial<UpdatedListingSearchInput>) => {
            let isValid = true
            if (!isAEAllProductsToggled && isForDivisionView) {
                // If there are no valid facets for division view, divisions are not valid
                if (!searchInput?.facets?.length) {
                    isValid = false
                } else {
                    const divisionFacetsCount =
                        (searchInput.facets?.find(
                            (facet) => facet.name === 'assemblyNodeId'
                        )?.values?.length || 0) +
                        (searchInput.facets?.find(
                            (facet) => facet.name === 'SectionNames'
                        )?.values?.length || 0) +
                        (searchInput.facets?.find(
                            (facet) => facet.name === 'specAgentProductTypeId'
                        )?.values?.length || 0)
                    isValid = divisionFacetsCount > 0
                }
            }
            return isValid
        },
        [isForDivisionView, isAEAllProductsToggled]
    )


    // Helper to compare search parameters (deep comparison)
    const searchParamsChanged = React.useCallback(
        (
            newParams: Partial<UpdatedListingSearchInput>,
            oldParams: Partial<UpdatedListingSearchInput> | null
        ) => {
            if (!oldParams) return true // First fetch
            return !isEqual(newParams, oldParams)
        },
        []
    )

    // Refetch - Only fetches data for the currently active tab
    const doRefetch = React.useCallback(() => {
        const searchInput = getSearchInput()
        const divValid = isDivisionFacetsValid(searchInput)
        // Fix for initial loading issue when in division view
        if (productsPage || divValid) {
            if (dialogTab === 0) {
                // Check if search params changed for this tab
                if (!searchParamsChanged(searchInput, lastSearchParamsRef.current)) {
                    setRefetch(false)
                    return
                }

                // Fetch main search products (Tab 0)
                setSearchLoading(true)
                setCurrentTotal(PER_PAGE) // Reset pagination so InfiniteLoader can trigger load-more after refetch
                // Don't use isAddElementsLoading here - it shows fullscreen spinner
                // Instead, rely on searchLoading + empty mergedProducts to show skeleton cards

                // Store search input for Phase 2 (accessed in onCompleted callback)
                currentSearchInputRef.current = searchInput
                lastSearchParamsRef.current = searchInput // Track last params
                // Reset BOD guard so onCompleted fires getMultiplePTsWithBOD once for this search
                bodCalledForCurrentSearchRef.current = false

                // Trigger Phase 1 minimal fetch
                getListingsMinimal({
                    variables: {
                        manufacturerProductSearchInput: searchInput,
                    },
                })

                // Mark search tab as fetched
                setTabsFetched(prev => ({ ...prev, search: true }))

                // Also fetch related tab count (minimal query for count only, no Phase 2)
                // Use baseline facets from workspace context, not UI-applied filters
                const baselineFacets = defaultSearchFacetInputs()

                const relatedProductsInput = {
                    ...manufacturerProductSearchInput,
                    facets: getRelatedProductsFacets(baselineFacets),
                    productTypeSelections: [],
                    logEventSource: LogEventSource.AeRelatedProducts,
                }

                // Fetch minimal for count, but don't mark as fetched (so Phase 2 runs when tab switched)
                getRelatedProductsMinimal({
                    variables: {
                        manufacturerProductSearchInput: relatedProductsInput,
                    },
                })
            } else if (dialogTab === 1) {
                // For related products tab, filter out listingLevels and withManufacturerOption from tab 0's facets
                // so that getRelatedProductsFacets can apply its own hardcoded listingLevels
                const updatedSearchFacetInputs = [
                    ...(manufacturerProductSearchInput?.facets || []),
                ].flat().filter((facet) => facet.name !== 'listingLevels' && facet.name !== 'withManufacturerOption')

                const relatedProductsInput = {
                    ...manufacturerProductSearchInput,
                    facets: getRelatedProductsFacets(
                        updatedSearchFacetInputs
                    ),
                    productTypeSelections: [],
                    logEventSource: LogEventSource.AeRelatedProducts,
                }

                // Check if related params changed for this tab
                if (!searchParamsChanged(relatedProductsInput as Partial<UpdatedListingSearchInput>, lastRelatedParamsRef.current)) {
                    setRefetch(false)
                    return
                }

                // Fetch related products (Tab 1)
                setSearchLoading(true)
                setCurrentRTotal(PER_PAGE) // Reset pagination so InfiniteLoader can trigger load-more after refetch

                // Store related search input for Phase 2
                currentRelatedSearchInputRef.current = relatedProductsInput as Partial<UpdatedListingSearchInput>
                lastRelatedParamsRef.current = relatedProductsInput as Partial<UpdatedListingSearchInput>// Track last params

                // Trigger Phase 1 minimal fetch for related products
                getRelatedProductsMinimal({
                    variables: {
                        manufacturerProductSearchInput: relatedProductsInput,
                    },
                })

                // Mark related tab as fetched
                setTabsFetched(prev => ({ ...prev, related: true }))
            }

            setRefetch(false)
        }
    }, [
        getSearchInput,
        manufacturerProductSearchInput,
        getListingsMinimal,
        getRelatedProductsMinimal,
        getRelatedProductsFacets,
        defaultSearchFacetInputs,
        productsPage,
        isDivisionFacetsValid,
        dialogTab,
        searchParamsChanged,
        // NOTE: tabsFetched NOT in deps to avoid circular dependency (we use functional setState)
    ])

    // Fetch data when tab changes (only if tab hasn't been fetched yet)
    React.useEffect(() => {
        if (dialogTab === 0 && !tabsFetched.search && !searchLoading) {
            // User switched to search tab and it hasn't been fetched
            setRefetch(true)
        } else if (dialogTab === 1 && !tabsFetched.related && !searchLoading) {
            // User switched to related tab and it hasn't been fetched
            setRefetch(true)
        }
    }, [dialogTab, tabsFetched, searchLoading])

    // Merge Phase 1 minimal results with cached store data
    // IMPORTANT: Always use minimalData as source of truth for which items to display
    // Phase 2 only updates the store, doesn't replace the list (since it uses listingIdsList filtering)
    const mergedProducts = React.useMemo(() => {
        // Show skeleton cards during initial search loading
        // Prevents stale cached cards from flashing before new results arrive
        if (searchLoading) return []

        // Use Phase 1 minimal data as the master list of IDs
        if (minimalData?.listingSearch?.matches) {
            const minimalListings = minimalData.listingSearch.matches
            const mergedList: Partial<ListingMatch>[] = []

            minimalListings.forEach((minimal) => {
                const cached = productListings[minimal.listingId]
                if (cached) {
                    // Use cached full data if available (updated by Phase 2)
                    mergedList.push(cached)
                } else {
                    // Use minimal data as placeholder until Phase 2 completes
                    // Skeleton cards will show for items without full data
                    mergedList.push(minimal)
                }
            })

            return [...new Set(mergedList)]
        }

        return []
    }, [minimalData, productListings, searchLoading])

    // Merge Phase 1 minimal results with cached store data for related products
    // IMPORTANT: Always use minimalData as source of truth for which items to display
    const mergedRelatedProducts = React.useMemo(() => {
        // Show skeleton cards during initial search loading
        // Prevents stale cached cards from flashing before new results arrive
        if (searchLoading) return []

        // Only return data if we're on the related products tab (tab 1)
        // On tab 0, we fetch minimal data for count only, but shouldn't display cards
        if (dialogTab !== 1) {
            return []
        }

        // Use Phase 1 minimal data as the master list of IDs
        if (relatedMinimalData?.listingSearch?.matches) {
            const minimalListings = relatedMinimalData.listingSearch.matches
            const mergedList: Partial<ListingMatch>[] = []

            minimalListings.forEach((minimal) => {
                const cached = productListings[minimal.listingId]
                if (cached) {
                    // Use cached full data if available (updated by Phase 2)
                    mergedList.push(cached)
                } else {
                    // Use minimal data as placeholder until Phase 2 completes
                    // Skeleton cards will show for items without full data
                    mergedList.push(minimal)
                }
            })

            return [... new Set(mergedList)]
        }

        return []
    }, [relatedMinimalData, productListings, dialogTab, searchLoading])

    // Get count from Phase 1 minimal data (ALWAYS prefer minimal as it has the true total)
    // Phase 2 with listingIdsList returns filtered count, not total
    const productsCount = React.useMemo(() => {
        return minimalData?.listingSearch?.count ?? searchData?.listingSearch?.count ?? 0
    }, [minimalData, searchData])

    const relatedProductsCount = React.useMemo(() => {
        return relatedMinimalData?.listingSearch?.count ?? relatedProducts?.listingSearch?.count ?? 0
    }, [relatedMinimalData, relatedProducts])

    // handleLoadMore - Pagination with two-phase fetch (moved here to access mergedProducts, getSearchInput, etc.)
    const handleLoadMore = React.useCallback(
        (_startIndex: number, stopIndex: number) => {
            // Prevent pagination during initial load
            if (!hasMountedRef.current) {
                return null
            }

            // Prevent pagination if still loading initial data (tab-specific)
            if (dialogTab === 0 && (minimalLoading || getListingsLoading)) {
                return null
            }

            if (dialogTab === 1 && (relatedMinimalLoading || getrelatedProductsLoading)) {
                return null
            }

            if (
                dialogTab === 0 &&
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                hasNextPage &&
                mergedProducts?.length > 0 &&  // Must have data loaded
                mergedProducts?.length >= PER_PAGE &&  // Must have loaded at least one full page
                mergedProducts?.length === currentTotal &&
                !!minimalFetchMore
            ) {
                setSearchFetchMoreLoading(true)
                setCurrentTotal((prev) => prev + OPTIMISTIC_CARDS)

                // Phase 1: Fetch minimal data for next page
                minimalFetchMore({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...getSearchInput(),
                            skip: mergedProducts?.length,
                            top: PER_PAGE,
                        },
                    },
                    updateQuery: (prev: any, { fetchMoreResult }: any) => {
                        if (!fetchMoreResult) {
                            setSearchFetchMoreLoading(false)
                            return prev
                        }

                        // Accumulate minimal listings (IDs + timestamps)
                        const newMinimalListings = fetchMoreResult.listingSearch.matches

                        // Check which ones need full data
                        const listingsToFetch = checkStaleListings(newMinimalListings)

                        if (listingsToFetch.length > 0) {
                            // Phase 2: Fetch full data for stale items via getListings.
                            // Using getListings directly (not searchFetchMore) works regardless
                            // of whether the initial full query was ever called.
                            // Facets are handled in Phase 1 (getListingsMinimal.onCompleted), so
                            // getListings.onCompleted is safe to call here without side effects.
                            getListings({
                                variables: {
                                    manufacturerProductSearchInput: {
                                        ...getSearchInput(),
                                        listingIdsList: listingsToFetch,
                                    },
                                },
                            })
                        } else {
                            // All cached, no Phase 2 needed
                            setSearchFetchMoreLoading(false)
                        }

                        // Return accumulated minimal data
                        return {
                            listingSearch: {
                                ...prev.listingSearch,
                                count: fetchMoreResult.listingSearch.count,
                                matches: [
                                    ...prev.listingSearch.matches!,
                                    ...fetchMoreResult.listingSearch.matches!,
                                ],
                            },
                        }
                    },
                })

                return null
            } else if (
                dialogTab === 1 &&
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                rHasNextPage &&
                mergedRelatedProducts?.length > 0 &&  // Must have data loaded
                mergedRelatedProducts?.length >= PER_PAGE &&  // Must have loaded at least one full page
                mergedRelatedProducts?.length ===
                currentRTotal &&
                !!relatedMinimalFetchMore
            ) {
                setSearchFetchMoreLoading(true)
                setCurrentRTotal((prev) => prev + OPTIMISTIC_CARDS)

                // Filter out listingLevels and withManufacturerOption from tab 0's facets for related products
                const filteredFacets = (manufacturerProductSearchInput?.facets ?? [])
                    .filter((facet) => facet.name !== 'listingLevels' && facet.name !== 'withManufacturerOption')

                const relatedInput = {
                    facets: getRelatedProductsFacets(
                        filteredFacets as Partial<FacetItem>[]
                    ),
                    productTypeSelections: [],
                    skip: mergedRelatedProducts?.length,
                    top: PER_PAGE,
                    logEventSource: LogEventSource.AeRelatedProducts,
                }

                // Phase 1: Fetch minimal data for next page
                relatedMinimalFetchMore({
                    variables: {
                        manufacturerProductSearchInput: relatedInput,
                    },
                    updateQuery: (prev: any, { fetchMoreResult }: any) => {
                        if (!fetchMoreResult) {
                            setSearchFetchMoreLoading(false)
                            return prev
                        }

                        // Check which ones need full data
                        const newMinimalListings = fetchMoreResult.listingSearch.matches
                        const listingsToFetch = checkStaleListings(newMinimalListings)

                        if (listingsToFetch.length > 0) {
                            // Phase 2: Fetch full data for stale items via getRelatedProducts.
                            // Using getRelatedProducts directly (not getMoreRelatedProducts) works
                            // regardless of whether the initial full query was ever called.
                            getRelatedProducts({
                                variables: {
                                    manufacturerProductSearchInput: {
                                        ...relatedInput,
                                        listingIdsList: listingsToFetch,
                                    },
                                },
                            })
                        } else {
                            setSearchFetchMoreLoading(false)
                        }

                        // Return accumulated minimal data
                        return {
                            listingSearch: {
                                ...prev.listingSearch,
                                count: fetchMoreResult.listingSearch.count,
                                matches: [
                                    ...prev.listingSearch.matches!,
                                    ...fetchMoreResult.listingSearch.matches!,
                                ],
                            },
                        }
                    },
                })

                return null
            }
            return null
        },
        [
            manufacturerProductSearchInput,
            searchLoading,
            searchFetchMoreLoading,
            refetch,
            mergedProducts,
            mergedRelatedProducts,
            currentTotal,
            currentRTotal,
            columnCount,
            hasNextPage,
            rHasNextPage,
            minimalFetchMore,
            getListings,
            relatedMinimalFetchMore,
            getRelatedProducts,
            checkStaleListings,
            setProductListings,
            setSearchFetchMoreLoading,
            setCurrentTotal,
            getSearchInput,
            getRelatedProductsFacets,
            dialogTab,
            category,
        ]
    )

    const handleListingType = React.useCallback((value: string[]) => {
        setListingType(value)
        setSearchFacetInputs((past) => {
            const filtered = past.filter((each) => each.name !== 'listingLevels')
            // When no listing types are selected, remove the facet entirely so the
            // API applies no listingLevels filter and returns all levels (including premium).
            // Adding listingLevels: [] would cause the API to exclude premium products.
            if (value.length === 0) return filtered
            return [
                ...filtered,
                {
                    name: 'listingLevels',
                    values: value,
                },
            ]
        })

        // Clear selected manufacturers when Manufacturer filter is deselected
        // Use functional update to avoid dependency on selectedManufacturers
        setSelectedManufacturers((current) =>
            !value.includes('Manufacturer') && current.length > 0 ? [] : current
        )
    }, [])

    const handleProduct = React.useCallback(
        (value: Record<string, boolean>) => {
            setProduct(value)
        },
        []
    )

    const handleSpecialtyClick = useCallback(
        (value: string[]) => {
            setSpecialty(value)
            if (value.includes('Manufacturer Options')) {
                setManufacturerProductSearchInput((past) => ({
                    ...past,
                    facets: [
                        ...(past?.facets ?? []),
                        { name: 'withManufacturerOption', values: ['true'] },
                    ],
                }))
            } else {
                setManufacturerProductSearchInput((past) => ({
                    ...past,
                    facets: (past?.facets ?? []).filter(
                        (facet) => facet.name !== 'withManufacturerOption'
                    ),
                }))
            }

            setTabsFetched({ search: false, related: false })
            setRefetch(true)
            setCurrentTotal(PER_PAGE) // Show full page of skeleton cards during loading
        },
        []
    )

    const handlePartershipSelection = React.useCallback(
        (type: PartnershipType, value: string[]) => {
            if (type === 'Sustainability Resources') {
                setSustainabilityResources(value)
            } else {
                setBIMResources(value)
            }
        },
        [setSustainabilityResources, setBIMResources]
    )

    const handleSelectUnselectAllManufacturers = React.useCallback(() => {
        const isDynamic = processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1'
        const isReviewer = userDetails.isReviewer

        const selectableListings = isReviewer && isDynamic
            ? (mergedProducts ?? []).filter((listing) => {
                if (!listing.productType) return false
                return isProductTypeInProject(listing.productType, {
                    isInProject,
                    selectedProductType: productType,
                    productTypesInProject: projectPTNames,
                })
            })
            : mergedProducts ?? []

        const newSelectedManufacturers = selectUnselectAllManufacturers(
            selectedManufacturers,
            selectableListings,
            productSelectedListingIds
        )
        setSelectedManufacturers(newSelectedManufacturers)
    }, [
        selectedManufacturers,
        setSelectedManufacturers,
        mergedProducts,
        productSelectedListingIds,
        userDetails.isReviewer,
        isInProject,
        productType,
        projectPTNames,
    ])

    const handleUpdateWorkspaceManufacturerBP = (
        input: WorkspaceManufacturerListingItem
    ) => {
        const listingMatch = mergedProducts?.find(
            (each: Partial<ListingMatch>) => each.listingId === input?.listingId
        )
        // updating the local state to reflect changes in real time
        if (listingMatch) {
            const selectedListIndex = productSelected?.findIndex(
                (product: ProductListingSelected) =>
                    product?.listingId === listingMatch?.listingId
            )
            if (selectedListIndex !== -1) {
                productSelected[selectedListIndex].isBestPractice = input.isBP
                productSelected[selectedListIndex].isGlobalBestPractice =
                    input.isGBP
            }
            setProductSelected([...productSelected])

            // Persist BP/GBP to Zustand so checkStaleListings sees it as fresh on next load.
            setProductListings([
                {
                    ...listingMatch,
                    isBestPractice: input.isBP,
                    isGlobalBestPractice: input.isGBP,
                },
            ])
        }
        serviceWorker?.controller?.postMessage({
            [WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]: {
                ...input,
            } as WorkspaceManufacturerListingItem,
        })
    }

    const updateWorkspaceItems = (
        contentItems: ContentItem[],
        input: Partial<CreateProjectManufacturerProductListingInput>,
        ptNodes?: ProjectElementNode[]
    ) => {
        serviceWorker?.controller?.postMessage({
            [WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY]: {
                listingId: input.listingId,
                contentItems: contentItems,
                isForBOD: input?.isBOD,
                elementId: input?.elementId,
                ptProjectElementNodes: ptNodes,
            } as WorkspaceManufacturerContentItems,
        })
        setTabsFetched({ search: false, related: false })
        setRefetch(true)
    }

    const updateWorkspaceElementTree = (
        projectElementNodes: ProjectElementNode[],
        updatedElementId?: string
    ) => {
        serviceWorker?.controller?.postMessage({
            [WORKSPACE_TREE_ELEMENT_NODES]: {
                projectElementNodes: projectElementNodes,
                updatedElementId: updatedElementId,
            } as WorkspaceTreeElementNodes,
        })
        getSelectedProducts()
    }

    const addListingToProject = React.useCallback(
        (input: Partial<CreateProjectManufacturerProductListingInput> & {
            addProprietaryListingDetail?: AddProprietaryListingDetailInput
        }) => {
            setIsAddElementsLoading(true)

            const proceedWithListingElements = (
                nuggetProjectElementNodes?: ProjectElementNode[]
            ): Promise<void> => {
                return addListingElements({ variables: { input, projectId } })
                    .then((value) => {
                        const errorCheck = checkPPLErrors(value)
                        if (errorCheck) return errorCheck

                        if (
                            value?.data?.addManufacturerProductListingElements
                                .projectElementNodes === null &&
                            value?.data?.addManufacturerProductListingElements
                                .updatedInput === null
                        ) {
                            showGlobalSnackbar?.({
                                variables: {
                                    message:
                                        'Product Listing Product Type not found',
                                    variant: 'error',
                                    id: `ProductListingDialog-AddProductListingElementsNotFound`,
                                },
                            })
                            return Promise.reject(new Error('Product Listing Product Type not found'))
                        } else if (nuggetProjectElementNodes) {
                            // Merge nugget response into the addManufacturerProductListingElements response
                            if (
                                value?.data
                                    ?.addManufacturerProductListingElements
                            ) {
                                const existingNodes =
                                    value.data
                                        .addManufacturerProductListingElements
                                        .projectElementNodes || []
                                value.data.addManufacturerProductListingElements.projectElementNodes = [
                                    ...nuggetProjectElementNodes,
                                    ...existingNodes,
                                ]
                            }
                        }

                        return Promise.resolve()
                    })
                    .catch((error) => {
                        // Re-throw to ensure promise chain properly rejects
                        return Promise.reject(error)
                    })
            }

            // Always proceed directly with listing elements
            // projectElementNodes will be handled in addListingElements response
            return proceedWithListingElements()
        },
        [addListingElements, showGlobalSnackbar, projectId]
    )

    const deleteListingMutation = React.useCallback(
        (input: Partial<DeleteProjectManufacturerProductListingInput>) => {
            setIsAddElementsLoading(true)
            deleteListing({ variables: { input } })
                .then((value) => {
                    if (isProductType) {
                        serviceWorker?.controller?.postMessage({
                            [WORKSPACE_MANUFACTURERS_CONTENT_ITEMS_KEY]: {
                                listingId: input.listingId,
                                contentItems:
                                    value.data
                                        ?.deleteProjectManufacturerProductListing ?? [],
                            } as WorkspaceManufacturerContentItems,
                        })
                    }
                    setIsAddElementsLoading(false)
                    setTabsFetched({ search: false, related: false })
                    setRefetch(true)
                    getSelectedProducts()
                })
                .catch(() => {
                    setIsAddElementsLoading(false)
                })
        },
        [deleteListing, serviceWorker, getSelectedProducts, isProductType]
    )

    const getNodenEleIDs = (
        listingMatch: Partial<ListingMatch> | undefined,
        selectedPT: Partial<SearchFacets> | undefined
    ) => {
        const matchElement = productTypeFacets.find(
            (each) => each.parentProductFamily === listingMatch?.sectionName
        )
        return {
            listingMatchNodeId: selectedPT
                ? selectedPT.pTNodeId
                : matchElement
                    ? matchElement.pTNodeId
                        ? matchElement.pTNodeId
                        : matchElement.familyNodeId
                    : nodeId,
            listingMatchElementId: selectedPT
                ? selectedPT.pTElementId
                : matchElement
                    ? matchElement.pTElementId
                        ? matchElement.pTElementId
                        : matchElement.familyElementId
                    : elementId,
        }
    }

    const getProjectNuggetIdAndFamilyIdFromFacets = React.useCallback(
        (facets: Partial<SearchFacets>[] | undefined) => {
            return {
                projectNuggetId: facets?.find(
                    (node) => node.pTElementId !== null
                )?.pTElementId,
                familyElementId: facets?.find(
                    (node) => node.familyElementId !== null
                )?.familyElementId,
            }
        },
        []
    )

    const getProductTypeFacetFromListing = (
        facets: Partial<SearchFacets>[] | undefined,
        listing: Partial<ListingMatch> | undefined
    ) => {
        // Try to match both product type name and ID in order to correctly
        // identify PT copies (they have different name, same ID);
        // failing that, just match PT ID (PT name might not match listing
        // if PT was renamed)
        return (
            facets?.find(
                (item) =>
                    Number(item?.specAgentProductTypeId) ===
                    Number(listing?.specAgentProductTypeId) &&
                    item?.value == listing?.productType
            ) ??
            facets?.find(
                (item) =>
                    Number(item?.specAgentProductTypeId) ===
                    Number(listing?.specAgentProductTypeId)
            )
        )
    }

    const handleAddProductOption = React.useCallback(
        (
            listingId: string,
            selectionType: SelectionType,
            withManufacturerOption: boolean,
            withMasterSpecOption: boolean,
            displayTextFormat: DisplayTextFormatType,
            addProprietaryListingDetail?: AddProprietaryListingDetailInput
        ) => {
            const listingMatch = mergedProducts?.find(
                (each: Partial<ListingMatch>) => each.listingId === listingId
            ) || productListings[listingId]

            if (listingMatch) {
                updateSelectedListingId(listingId)

                const isSelectedItemFirstBOD =
                    productSelected?.filter(
                        (item) => item?.selectionType === 'BOD'
                    )?.length === 0 && selectionType === 'BOD'
                        ? true
                        : false

                const selectedPT = getProductTypeFacetFromListing(
                    productTypeFacets,
                    listingMatch
                )

                const {
                    listingMatchNodeId,
                    listingMatchElementId,
                } = getNodenEleIDs(listingMatch, selectedPT)

                let selectedNodeId =
                    isProductType || isProductFamily
                        ? nodeId
                        : listingMatchNodeId

                // Get the family element ID from projectNuggetData (most reliable source for PT view)
                const nuggetProductFamilyId =
                    projectNuggetData?.getProjectNugget
                        ?.projectProductTypePropertiesView?.productFamilyId

                // Resolve all complex conditional logic using utility function
                const {
                    resolvedProductFamilyId,
                    resolvedProjectNuggetId,
                    resolvedProjectElementType,
                    resolvedProductType,
                    resolvedDisplayTextFormat,
                    resolvedPtpOptionsToInclude,
                } = resolveAddListingToProjectParams({
                    selectedPT,
                    isProductType,
                    isProductFamily,
                    nuggetProductFamilyId,
                    listingMatchElementId,
                    elementId,
                    listingMatchProductType: listingMatch.productType ?? '',
                    displayTextFormat,
                    withMasterSpecOption,
                    withManufacturerOption,
                })

                // Set dialogAdditionDataRef BEFORE mutation so onCompleted can use it
                if (isBetaWorkspaceToggleEnabled) {
                    dialogAdditionDataRef.current = {
                        listingId: listingMatch.listingId || "",
                        isSelected: true,
                        isFirstBOD: isSelectedItemFirstBOD,
                        listingText:
                            listingMatch.type ===
                                ListingLevel.PremiumMasterSpec ||
                                listingMatch.type === ListingLevel.Premium ||
                                listingMatch.type ===
                                ListingLevel.PremiumProprietary
                                ? `${listingMatch.firmName}:${listingMatch.productName}`
                                : `${listingMatch.firmName}`,
                        listingType: listingMatch.type
                            ?.split(' ')
                            .join('')
                            .toUpperCase() as ListingLevel || ListingLevel.None,
                        firmName: listingMatch.firmName || "",
                        isBOD: selectionType === 'BOD',
                        hasSupportedManufacturersOptions: withManufacturerOption,
                        elementId: elementId || "",
                        isBP: false,
                        isGBP: false,
                    }
                }

                // Call the mutation first, then update state only on success
                return addListingToProject({
                    firmName: listingMatch.firmName,
                    isBOD: selectionType === 'BOD',
                    listingId: listingMatch.listingId,
                    productFamilyId: resolvedProductFamilyId,
                    productName: listingMatch.productName,
                    productType: resolvedProductType,
                    projectId,
                    projectNuggetId: resolvedProjectNuggetId,
                    // At family level, listingMatchElementId is the family's entity ID (not a PT
                    // ProjectNugget). Pass the PT's nugget ID when available (PT filter active),
                    // otherwise empty string so BE creates the PT ProjectNugget from productType + productFamilyId.
                    elementId: isProductFamily ? (selectedPT?.pTElementId ?? '') : listingMatchElementId,
                    projectElementType: resolvedProjectElementType,
                    type: listingMatch.type?.split(' ').join('').toUpperCase(),
                    nodeId: selectedNodeId,
                    specAgentProductTypeId: listingMatch.specAgentProductTypeId?.toString(),
                    isAddMasterSpecAndManufacturerOptions: withManufacturerOption,
                    displayTextFormat: resolvedDisplayTextFormat,
                    ptpOptionsToInclude: resolvedPtpOptionsToInclude,
                    isProductFamilyGroupView: isDivisionViewFromTreePath,
                    addProprietaryListingDetail: addProprietaryListingDetail
                } as Partial<CreateProjectManufacturerProductListingInput>).then(() => {
                    // Only update state if the mutation succeeded
                    const selectedProductTemp = productSelected
                        .filter((each) => each.listingId !== listingMatch.listingId)
                        .map((item) => {
                            return {
                                ...item,
                                isBOD:
                                    item.isBOD && selectionType === 'BOD'
                                        ? false
                                        : item.isBOD,
                                selectionType: 'Product' as SelectionType,
                                isActive:
                                    item?.selectionType === 'BOD' &&
                                        selectionType === 'BOD'
                                        ? false
                                        : true,
                            }
                        })

                    setProductSelected([
                        ...selectedProductTemp,
                        {
                            ...listingMatch,
                            selectionType: selectionType,
                            isBOD: selectionType === 'BOD',
                            isActive: selectionType === 'BOD',
                            hasSupportedManufacturersOptions: withManufacturerOption,
                            elementId: elementId as string,
                        },
                    ])

                    // Remove the card from selectedManufacturers if it was ctrl+selected
                    setSelectedManufacturers((current) =>
                        current.filter((item) => item.listingId !== listingMatch.listingId)
                    )

                    // Update the product count and send to service worker
                    const newCount = (projectProductListingCount ?? 0) + 1
                    setProjectProductListingCount(newCount)
                    sendServiceWorkerMessage(newCount)

                    // For non-beta workspace, send service worker message immediately
                    if (!isBetaWorkspaceToggleEnabled) {
                        serviceWorker?.controller?.postMessage({
                            [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                                listingId: listingMatch.listingId,
                                isSelected: true,
                                isFirstBOD: isSelectedItemFirstBOD,
                                listingText:
                                    listingMatch.type ===
                                        ListingLevel.PremiumMasterSpec ||
                                        listingMatch.type === ListingLevel.Premium ||
                                        listingMatch.type ===
                                        ListingLevel.PremiumProprietary
                                        ? `${listingMatch.firmName}:${listingMatch.productName}`
                                        : `${listingMatch.firmName}`,
                                listingType: listingMatch.type
                                    ?.split(' ')
                                    .join('')
                                    .toUpperCase(),
                                firmName: listingMatch.firmName,
                                isBOD: selectionType === 'BOD',
                            } as WorkspaceManufacturerSelected,
                        })
                    }
                })
            }

            return Promise.reject(new Error('Listing not found'))
        },
        [
            productListings,
            serviceWorker,
            projectId,
            projectNuggetData,
            elementId,
            productSelected,
            setProductSelected,
            addListingToProject,
            setSelectedManufacturers,
            isProductFamily,
            isProductType,
            productTypeFacets,
            searchFacetInputs,
            getNodenEleIDs,
            getProductTypeFacetFromListing,
            isBetaWorkspaceToggleEnabled,
            projectProductListingCount,
            setProjectProductListingCount,
            sendServiceWorkerMessage,
            nodeId,
            isDivisionViewFromTreePath,
        ]
    )

    const handleRemoveProductOption = React.useCallback(
        (listingId: string, isBOD: boolean) => {
            const listingMatch = mergedProducts?.find(
                (each) => each.listingId === listingId
            ) || productListings[listingId]

            // Find the selected product to get the correct elementId that was stored when added
            const selectedProduct = productSelected.find(
                (each) => each.listingId === listingId
            )

            if (listingMatch) {
                updateSelectedListingId(listingId)
                const selectedPT = getProductTypeFacetFromListing(
                    productTypeFacets,
                    listingMatch
                )

                const {
                    listingMatchNodeId,
                    listingMatchElementId,
                } = getNodenEleIDs(
                    listingMatch ? listingMatch : ({} as Partial<ListingMatch>),
                    selectedPT
                )

                // Get productFamilyId from projectNuggetData (most reliable source), with facet fallback for old workspace
                const nuggetProductFamilyId =
                    projectNuggetData?.getProjectNugget
                        ?.projectProductTypePropertiesView?.productFamilyId
                const {
                    familyElementId: facetFamilyElementId,
                } = getProjectNuggetIdAndFamilyIdFromFacets(productTypeFacets)

                deleteListingMutation({
                    isBOD,
                    listingId,
                    productFamilyId:
                        nuggetProductFamilyId || facetFamilyElementId || '',
                    projectId,
                    // Prefer the elementId stored at add-time (selectedProduct) over the one
                    // derived from facets (listingMatchElementId) — the stored value is the actual
                    // project nugget element that must be targeted for deletion.
                    projectNuggetId: selectedProduct?.elementId || listingMatchElementId || undefined,
                    isRetainProperties: false, // TODO: Change value if there is removal warning
                    firmName: listingMatch?.firmName,
                    type: listingMatch?.type?.replace(/\s/g, '').toUpperCase(),
                    nodeId: listingMatchNodeId,
                } as DeleteProjectManufacturerProductListingInput)

                setProductSelected((past) => [
                    ...past.filter((each) => each.listingId !== listingId),
                ])
                serviceWorker?.controller?.postMessage({
                    [WORKSPACE_MANUFACTURER_SELECTED_KEY]: {
                        listingId: listingId,
                        isSelected: false,
                        hasSupportedManufacturersOptions: false,
                        isBOD,
                    } as WorkspaceManufacturerSelected,
                })
                const selectedListingCount = projectProductListingCount
                    ? projectProductListingCount - 1
                    : 0
                setProjectProductListingCount(selectedListingCount)
                sendServiceWorkerMessage(selectedListingCount)

                setManufacturerProductSearchInput((past) => ({
                    ...past,
                    projectId,

                    listingIdsList: past.listingIdsList?.filter(
                        (each) => each !== listingId
                    ),
                }))
            }
        },
        [
            isProductFamily,
            projectNuggetData,
            serviceWorker,
            deleteListingMutation,
            projectId,
            elementId,
            searchData,
            projectProductListingCount,
            setProjectProductListingCount,
            isProductType,
            sendServiceWorkerMessage,
            nodeId,
            autosuggestCardData,
            productTypeFacets,
            getNodenEleIDs,
            getProductTypeFacetFromListing,
            getProjectNuggetIdAndFamilyIdFromFacets,
            productSelected,
        ]
    )

    const handleModifyProductOption = (
        listingId: string,
        selectionType: SelectionType
    ) => {
        setProductSelected((past) => [
            ...past.map((each) => {
                if (each.listingId === listingId) {
                    return {
                        ...each,
                        selectionType,
                    }
                }
                return each
            }),
        ])
    }

    const unloadListener = React.useCallback(() => {
        updatePersistentConfigs(
            userId,
            projectId,
            'projectProductDialogOpened',
            false
        )
    }, [projectId, userId])

    const updateSelectedListingId = React.useCallback(
        (listingId) => {
            updatePersistentConfigs(
                userId,
                projectId,
                'selectedListingId',
                listingId
            )
        },
        [projectId, userId]
    )
    const getSelectedListingId = React.useCallback(() => {
        return getFromLocalStorage(userId!, projectId!, 'selectedListingId')
    }, [projectId, userId])

    const getFacetSelectedOptions = (facet: ProductListingDialogFacet) => {
        const filtered = searchFacetInputs.find((each) => each.name === facet)
        if (facet === 'ProductType') {
            const productTypeValues = getProductTypeSelectionsData(
                productTypeFacets,
                searchFacetInputs
            )
            return productTypeValues?.map((each) => {
                return `${each?.value} || ${each?.parentProductFamily}`
            })
        }
        return filtered?.values.sort((a, b) => a.localeCompare(b)) || []
    }

    const getFacetOptions = (facet: ProductListingDialogFacet) => {
        const filtered = searchFacets.find((each) => each.name === facet)

        let addedToProjectItems = searchData?.listingSearch.matches?.filter(
            (matchedListing) =>
                productSelectedListingIds.some(
                    (id) => id === matchedListing.listingId
                )
        )

        // When "Added to Project" filter is active for FirmName, narrow options to
        // only manufacturers present in the current search results.
        // Otherwise return all values for the facet.
        // In both paths: deduplicate (API can return repeated parentProductFamily
        // strings) then sort alphabetically.
        return product[productOptions.ADDED_TO_PROJECT] && facet === 'FirmName'
            ? filtered?.values
                ?.filter((filteredItem) =>
                    addedToProjectItems?.some(
                        (addedToProdectListing) =>
                            addedToProdectListing.firmName ===
                            filteredItem.value
                    )
                )
                ?.map((each) => each.value!)
                .filter((v, i, self) => self.indexOf(v) === i)
                .sort((a, b) => a.localeCompare(b)) || []
            : filtered?.values
                ?.map((each) => each.value!)
                .filter((v, i, self) => self.indexOf(v) === i)
                .sort((a, b) => a.localeCompare(b)) || []
    }

    const getProductFacetOptions = React.useCallback(
        (facet: ProductListingDialogFacet) => {
            // Get currently selected families from the filter
            const selectedFamilies = searchFacetInputs.find(
                (input) => input.name === 'SectionNames'
            )?.values || []

            const allProductTypes =
                autosuggestCardData?.listingCardsAutoSuggest?.productFamilyToProductTypes?.map(
                    (each) => ({
                        value: each?.productType ?? '',
                        parentProductFamily: each?.parentProductFamily ?? '',
                        specAgentProductTypeId:
                            each?.specAgentProductTypeId ?? '',
                        pTElementId: each.pTElementId,
                        pTNodeId: each.pTNodeId,
                        familyElementId: each.familyElementId,
                        familyNodeId: each.familyNodeId,
                    })
                ) || []

            // Filter product types based on selected families
            const filteredProductTypes =
                selectedFamilies.length > 0
                    ? allProductTypes.filter((pt) =>
                        selectedFamilies.some(
                            (family) =>
                                family.trim() ===
                                pt.parentProductFamily?.trim()
                        )
                    )
                    : allProductTypes

            return filteredProductTypes.sort((a, b) =>
                a.value.localeCompare(b.value)
            )
        },
        [autosuggestCardData, searchFacetInputs]
    )

    const handleFacetSelectedChange = React.useCallback(
        (
            facet: ProductListingDialogFacet,
            value: string[],
            productTypeFacets?: Partial<SearchFacets>[],
            inputValue?: string
        ) => {
            if (
                productTypeFacets &&
                productTypeFacets?.length !== 0 &&
                !autosuggestCardLoading
            ) {
                setProductTypeFacets((past) => {
                    return removeDuplicateProductTypeFacets([
                        ...productTypeFacets,
                        ...past,
                    ])
                })
            }
            setSearchFacetInputs((past) => {
                return [
                    ...past.filter(
                        (each) =>
                            each.name?.toLowerCase() !== facet?.toLowerCase()
                    ),
                    {
                        name: facet,
                        values: value,
                    },
                ]
            })
            setManufacturerProductSearchInput((past) => ({
                ...past,
                projectId,

                listingIdsList: past.listingIdsList?.filter(
                    (each) => each !== listingId
                ),
            }))
        },
        [autosuggestCardLoading, listingId, setProductTypeFacets]
    )

    const setHeaderPillToggle = React.useCallback(
        (isToggled: boolean) => {
            if (isToggled !== isAEAllProductsToggled) {
                getSelectedProducts()
                getProductFlaggedForReviewProducts()
                setIsAEAllProductsToggled(isToggled)
                setSpecialty([])
                if (!!isToggled) {
                    // All Products / Clear filters
                    handleFacetSelectedChange?.('SectionNames', [] as string[])
                    handleFacetSelectedChange?.('ProductType', [] as string[])
                } else {
                    // Assembly / Restore PF and PT filter selections if any
                    if (hasFamily && !!family) {
                        handleFacetSelectedChange?.('SectionNames', [
                            family as string,
                        ])
                    } else {
                        handleFacetSelectedChange?.(
                            'SectionNames',
                            [] as string[]
                        )
                    }
                    if (hasProductType && !!productType) {
                        handleFacetSelectedChange?.('ProductType', [
                            productType as string,
                        ])
                    } else {
                        handleFacetSelectedChange?.(
                            'ProductType',
                            [] as string[]
                        )
                    }
                }
                handleFacetSelectedChange?.('FirmName', [] as string[])
                // Clear advance filters
                handleProduct(defaultProduct) // 'All Listings'
                handlePartershipSelection?.(
                    'Sustainability Resources',
                    [] as string[]
                )
                handlePartershipSelection?.('BIM Resources', [] as string[])
                handleListingType([] as string[])
            }
        },
        [
            isAEAllProductsToggled,
            getSelectedProducts,
            getProductFlaggedForReviewProducts,
            setIsAEAllProductsToggled,
            handleFacetSelectedChange,
            handleProduct,
            handlePartershipSelection,
            handleListingType,
            hasFamily,
            family,
            hasProductType,
            productType,
        ]
    )

    const apolloClient = useApolloClient()

    const [parentAssemblyNodeId, setParentAssemblyNodeId] = React.useState<
        string | null
    >(null)

    // Function to get the parent assembly node
    const getParentAssemblyNode = React.useCallback(
        async (currentNodeId: string) => {
            if (!projectId) return null

            try {
                const result = await apolloClient.query({
                    query: GetProjectElementNodesQuery,
                    variables: {
                        input: {
                            projectId: projectId,
                            skip: 0,
                            limit: 10000,
                            projectElementType: [
                                ProjectElementType.ASSEMBLY,
                                ProjectElementType.PRODUCTFAMILY,
                                ProjectElementType.PRODUCTTYPE,
                            ],
                            onlyElementsAssignedToProject: true,
                            isFromAEWorkSpace: true,
                        },
                    },
                    fetchPolicy: 'no-cache',
                })

                const data = result.data as GetProjectElementNodesResponse
                const allNodes =
                    data?.getProjectElementNodes?.projectElementNodes || []

                // Find the current node
                const currentNode = allNodes.find(
                    (node: any) => node.id === currentNodeId
                )

                // Return its parentNodeId
                return currentNode?.parentNodeId || null
            } catch (error) {
                console.error('Error querying parent assembly node:', error)
                return null
            }
        },
        [projectId, apolloClient]
    )

    // Function to get all family nodeIds under current assembly
    const getAllFamilyNodeIdsUnderAssembly = React.useCallback(async () => {
        if (!nodeId || !projectId) return null

        try {
            const result = await apolloClient.query({
                query: GetProjectElementNodesQuery,
                variables: {
                    input: {
                        projectId: projectId,
                        parentNodeId: nodeId, // Current assembly
                        projectElementType: [
                            ProjectElementType.ASSEMBLY,
                            ProjectElementType.PRODUCTFAMILY,
                        ],
                        onlyElementsAssignedToProject: true,
                        isFromAEWorkSpace: true,
                        skip: 0,
                        limit: 1000,
                        categoryId: undefined,
                        isProductFamilyGroupView: false,
                        searchFilters: undefined,
                    },
                },
                fetchPolicy: 'no-cache',
            })

            const data = result.data as GetProjectElementNodesResponse
            const familyNodes = data?.getProjectElementNodes?.projectElementNodes?.filter(
                (node: any) =>
                    node.elementType === ProjectElementType.PRODUCTFAMILY
            )

            return familyNodes?.map((node: any) => node.id) || null
        } catch (error) {
            console.error('Error fetching family nodes:', error)
            return null
        }
    }, [nodeId, projectId, apolloClient])

    // Extract and cache parent assembly node when clicking on family/PT elements
    React.useEffect(() => {
        let isMounted = true
        const currentNodeId = nodeId

        const fetchParent = async () => {
            let result: string | null = null

            if ((isProductFamily || isProductType) && nodeId && !parentNodeId) {
                // Clicked on family/PT without parentNodeId in URL - query for it
                result = await getParentAssemblyNode(nodeId)
            } else if (parentNodeId) {
                // parentNodeId exists in URL - use it directly
                result = parentNodeId
            } else {
                // Assembly or other element
                result = null
            }

            // Only update if still mounted and nodeId hasn't changed
            if (isMounted && currentNodeId === nodeId) {
                setParentAssemblyNodeId(result)
            }
        }

        fetchParent()

        return () => {
            isMounted = false
        }
    }, [
        nodeId,
        parentNodeId,
        isProductFamily,
        isProductType,
        getParentAssemblyNode,
    ])

    // Paginated fetch of all direct children under a given parentNodeId.
    // totalCount is unknown before the first response, so it is initialised to 0 and
    // updated from the first page — the do-while always executes at least once.
    const fetchNodes = React.useCallback(
        async (
            parentNodeId: string,
            signal: { isMounted: boolean }
        ): Promise<ProjectElementNodeWithMetadata[]> => {
            const PAGE_SIZE = 1000
            const accumulated: ProjectElementNodeWithMetadata[] = []
            let skip = 0
            let totalCount = 0

            do {
                if (!signal.isMounted) break

                const { data } = await apolloClient.query({
                    query: GetProjectElementNodesQuery,
                    variables: {
                        input: {
                            projectId,
                            parentNodeId,
                            projectElementType: [
                                ProjectElementType.ASSEMBLY,
                                ProjectElementType.PRODUCTFAMILY,
                                ProjectElementType.PRODUCTTYPE,
                            ],
                            onlyElementsAssignedToProject: true,
                            isFromAEWorkSpace: true,
                            skip,
                            limit: PAGE_SIZE,
                        },
                    },
                    fetchPolicy: 'cache-first',
                }) as { data: GetProjectElementNodesResponse }

                const page = data?.getProjectElementNodes?.projectElementNodes ?? []
                totalCount = data?.getProjectElementNodes?.totalCount ?? 0

                accumulated.push(...page)
                skip += PAGE_SIZE

                if (page.length === 0) break
            } while (skip < totalCount)

            return accumulated
        },
        [projectId, apolloClient]
    )

    // Fetch the actual PT names in the project tree under the current family or lowest assembly level.
    // This is used to check if a Manufacturer card's PT is actually in the project.
    const fetchProjectPTs = React.useCallback(
        async (signal: { isMounted: boolean }) => {
            if ((!isProductFamily && !isLastSubAssembly) || !nodeId || !projectId) {
                if (signal.isMounted) {
                    setProjectPTNames([])
                }
                return
            }

            try {
                const allPTNodes: ProjectElementNodeWithMetadata[] = []

                // For isLastSubAssembly, the query API only returns direct children.
                // PTs are grandchildren of the assembly (assembly → family → PT),
                // so we must first fetch the intermediate family nodes, then fetch PTs under each.
                let parentIdsForPTs: string[] = [nodeId]

                if (isLastSubAssembly && !isProductFamily) {
                    // Step 1: paginated fetch of all direct children of the assembly node.
                    // Must paginate because an assembly can have > PAGE_SIZE family nodes.
                    const assemblyChildren = await fetchNodes(nodeId, signal)

                    if (!signal.isMounted) return

                    // Collect any direct PT children of the assembly (edge case)
                    allPTNodes.push(
                        ...assemblyChildren.filter(
                            (n) => n.elementType === ProjectElementType.PRODUCTTYPE
                        )
                    )

                    // Use intermediate family node IDs as the parents for the PT fetch loop.
                    // If empty, the for-loop below simply doesn't execute and we fall through
                    // to the final setProjectPTNames with any direct PT children already collected.
                    parentIdsForPTs = assemblyChildren
                        .filter((n) => n.elementType === ProjectElementType.PRODUCTFAMILY)
                        .map((n) => n.id)
                        .filter(Boolean) as string[]
                }

                // Fetch PTs under each parent (family node for isLastSubAssembly, or the node itself)
                for (const parentId of parentIdsForPTs) {
                    if (!signal.isMounted) break

                    const nodes = await fetchNodes(parentId, signal)
                    allPTNodes.push(
                        ...nodes.filter((n) => n.elementType === ProjectElementType.PRODUCTTYPE)
                    )
                }

                // PT nodes use 'text' field for the name
                const ptNames = allPTNodes.map((node) => node.text).filter(Boolean)

                if (signal.isMounted) {
                    setProjectPTNames(ptNames)
                }
            } catch (error) {
                if (signal.isMounted) {
                    // Clear stale PT names on error — preserving them could mislead the UI
                    // into enabling add-to-project for PTs not in the current context.
                    setProjectPTNames([])
                    console.error('Error fetching project PT children:', error)
                }
            }
        },
        [isProductFamily, isLastSubAssembly, nodeId, projectId, fetchNodes]
    )

    React.useEffect(() => {
        const signal = { isMounted: true }
        fetchProjectPTs(signal)
        return () => {
            signal.isMounted = false
        }
    }, [fetchProjectPTs])

    const getSelectedFamilyNodeIds = React.useCallback(
        (facet: ProductListingDialogFacet) => {
            if (facet === 'ProductType') {
                const familyNames = searchFacetInputs.find(
                    (item) => item.name === 'SectionNames'
                )

                const selectedFamilies = productTypeFacets
                    .filter((fam) =>
                        familyNames?.values.some(
                            (each) =>
                                !!fam.familyNodeId &&
                                (isProductFamily
                                    ? true
                                    : nodeId !== fam.familyNodeId) &&
                                each.trim() === fam.parentProductFamily?.trim()
                        )
                    )
                    ?.reduce((prev: Partial<SearchFacets>[], current) => {
                        const isExist = prev.find(
                            (item) =>
                                item.parentProductFamily ===
                                current.parentProductFamily
                        )
                        if (!isExist) {
                            return prev.concat([current])
                        } else {
                            return prev
                        }
                    }, [])
                const familyNodeIds = selectedFamilies.flatMap(
                    (item) => item.familyNodeId
                )

                const uniquesFamilyNodeIds = Array.from(new Set(familyNodeIds))

                if (uniquesFamilyNodeIds.length > 0) {
                    return uniquesFamilyNodeIds
                } else if (isProductType && !!parentNodeId) {
                    // if AllProducts is toggled and no Family is selected, we are returning null, this will fetch 50 initial suggestsion
                    if (
                        isAEAllProductsToggled ||
                        (dialogTab === 1 && uniquesFamilyNodeIds.length === 0)
                    ) {
                        return null
                    } else {
                        //if no family node is selected and PT is selected, we are going to return the category node,
                        // Use category if no family is selected
                        return uniquesFamilyNodeIds.length === 0 &&
                            !!categoryNodeId
                            ? [categoryNodeId]
                            : [parentNodeId]
                    }
                } else if (nodeId && !isAEAllProductsToggled) {
                    //if we are using related products, we are going to treat this as all products
                    if (familyNodeIds.length === 0) {
                        //no selected family, we're going to get all the product types under the assembly
                        return null
                    }
                    return dialogTab === 1 ? null : [nodeId]
                } else {
                    return null
                }
            }

            if (isProductType && !!parentNodeId) {
                // if AllProducts is toggled and no Family is selected, we are returning null, this will fetch 50 initial data
                return isAEAllProductsToggled || dialogTab === 1
                    ? null
                    : [parentNodeId]
            } else if (nodeId) {
                // if AllProducts is toggled and no Family is selected, we are returning null, this will fetch 50 initial suggestsion
                return isAEAllProductsToggled || dialogTab === 1
                    ? null
                    : [nodeId]
            } else {
                return null
            }
        },
        [
            nodeId,
            productTypeFacets,
            searchFacetInputs,
            parentNodeId,
            isProductType,
            isProductFamily,
            dialogTab,
        ]
    )

    // Helper: Check if a filter facet has values
    const hasFacetWithValues = React.useCallback(
        (facetName: string) => {
            return searchFacetInputs.some(
                (f) =>
                    (f.name === facetName.toLowerCase() ||
                        f.name === facetName) &&
                    f.values &&
                    f.values.length > 0
            )
        },
        [searchFacetInputs]
    )

    // Helper: Get the correct nodeId for assembly filtering
    const getNodeIdForAssemblyFilter = React.useCallback(() => {
        // If clicked on family/PT: use parent assembly node
        // Otherwise: use the current nodeId
        if (isProductFamily || isProductType) {
            return parentAssemblyNodeId || parentNodeId || nodeId
        }
        return nodeId
    }, [
        isProductFamily,
        isProductType,
        parentAssemblyNodeId,
        parentNodeId,
        nodeId,
    ])

    const fetchAutoSuggest = React.useCallback(
        async (facet: ProductListingDialogFacet, value: string) => {
            setAutosuggestFacet({ name: facet, value })
            setSearchFacets((past) => [
                ...past.filter(
                    (each) => each.name?.toLowerCase() !== facet.toLowerCase()
                ),
            ])

            if (facet === 'ProductType' || facet === 'SectionNames') {
                // Get all family nodeIds under current assembly instead of selected ones
                const resolvedAssemblyNodeId = isProductType
                    ? getNodeIdForAssemblyFilter()
                    : null
                const allFamilyNodeIds =
                    elementType === 'ASSEMBLY'
                        ? await getAllFamilyNodeIdsUnderAssembly()
                        : resolvedAssemblyNodeId
                            ? [resolvedAssemblyNodeId]
                            : getSelectedFamilyNodeIds(facet)


                getListingCardAutosuggest({
                    variables: {
                        input: {
                            autoSuggestField:
                                facet === 'ProductType'
                                    ? ('PRODUCTTYPE' as AutoSuggestFieldType)
                                    : ('PRODUCTFAMILY' as AutoSuggestFieldType),
                            nodeIds: allFamilyNodeIds,
                            searchText: value,
                            limit: 50,
                            skip: 0,
                            projectId: !isAEAllProductsToggled
                                ? projectId!
                                : undefined,
                            partnerships: sustainabiliyResources,
                            firmName: searchFacetInputs?.find(
                                (facet) => facet?.name === 'FirmName'
                            )?.values,
                            isPremiumProprietary: false,
                            isForNonMasterspecFamily: dialogTab === 1,
                            divisionNumber: isAEAllProductsToggled
                                ? ''
                                : divisionNumber,
                            //when viewing related products, we're treating the autosuggest as for all products.
                            isForAllProducts:
                                dialogTab === 1 ? true : isAEAllProductsToggled,
                        },
                    },
                })
            } else {
                let tempFacets = [
                    ...searchFacetInputs.filter(
                        (each) =>
                            each.name?.toLowerCase() !== facet.toLowerCase()
                    ),
                ]

                // We need to set the listing level to premium proprietary to the facets
                // if the product listing dialog is on the related products tab
                if (dialogTab === 1) {
                    tempFacets = tempFacets.filter((facet) => {
                        return facet.name !== 'listingLevels'
                    })
                    tempFacets.push({
                        name: 'isForNonMasterspecFamily',
                        values: ['true'],
                    })
                }

                // Need to match case of API for this to work correctly.
                tempFacets = tempFacets.map((each) => {
                    let facet = { ...each }
                    if (each.name === 'ProductType') {
                        facet = { ...facet, name: 'productType' }
                    }
                    if (each.name === 'SectionNames') {
                        facet = { ...facet, name: 'sectionNames' }
                    }
                    return facet
                })

                // If no SectionNames and ProductType filters, add assemblyNodeId facet
                const hasSectionNamesFilter = hasFacetWithValues('SectionNames')
                const hasProductTypeFilter = hasFacetWithValues('ProductType')

                if (!hasSectionNamesFilter && !hasProductTypeFilter && nodeId) {
                    // Remove any existing assemblyNodeId first to avoid duplicates
                    tempFacets = tempFacets.filter(
                        (f) => f.name !== 'assemblyNodeId'
                    )

                    const nodeIdToUse = getNodeIdForAssemblyFilter()
                    const assemblyNodeId =
                        isDivisionSelected || !nodeIdToUse
                            ? undefined
                            : [nodeIdToUse]

                    if (assemblyNodeId && assemblyNodeId.length > 0) {
                        tempFacets.push({
                            name: 'assemblyNodeId',
                            values: assemblyNodeId,
                        })
                    }
                }

                getListingsAutosuggest({
                    variables: {
                        input: {
                            facets: tempFacets,
                            searchLimit: 50,
                            searchField: facet,
                            searchText: value,
                            productTypeSelections:
                                dialogTab === 1
                                    ? null
                                    : !isAEAllProductsToggled
                                        ? productTypeSelections
                                        : undefined,
                            projectId: !isAEAllProductsToggled
                                ? projectId
                                : undefined,
                        },
                    },
                })
            }
        },
        [
            elementType,
            isProductType,
            isProductFamily,
            productTypeSelections,
            searchFacetInputs,
            nodeId,
            parentNodeId,
            projectId,
            sustainabiliyResources,
            getAllFamilyNodeIdsUnderAssembly,
            getSelectedFamilyNodeIds,
            getListingCardAutosuggest,
            getListingsAutosuggest,
            dialogTab,
            divisionNumber,
            queryParams,
            isAEAllProductsToggled,
            hasFacetWithValues,
            getNodeIdForAssemblyFilter,
            isDivisionSelected,
        ]
    )

    const handleAutocompleteInputValueChange = React.useCallback(
        debounce(fetchAutoSuggest),
        [fetchAutoSuggest]
    )

    const handleAutocompleteNameChange = React.useCallback(
        (facet?: ProductListingDialogFacet) => {
            if (!!facet && !isProductTypeFacetsLoading) {
                fetchAutoSuggest(facet, '')
            }
        },
        [fetchAutoSuggest, isProductTypeFacetsLoading]
    )

    const debounceUpdate = React.useCallback(
        debounce(
            (workspaceEnabledPTPOptions: ProductTypePropertiesInputType[]) => {
                setProductTypeSelections(
                    defaultProductTypeSelections(
                        hasFamily,
                        family as string,
                        hasProductType,
                        productType as string,
                        workspaceEnabledPTPOptions
                    )
                )
                setOrderBy(defaultListingOrder)
                setPropertyOptions(workspaceEnabledPTPOptions)
            },
            500
        ),
        [
            hasFamily,
            family,
            hasProductType,
            productType,
            isAssembly,
            isLastSubAssembly,
        ]
    )
    const onRemoveProductListing = (listingId: string, isBOD: boolean) => {
        setListingInfoToDelete({ listingId, isBOD: isBOD ?? false })
        setIsRemoveWarningOpen(!isRemoveWarningOpen)
    }

    const triggerRemoveProductListing = () => {
        if (listingInfoToDelete) {
            handleRemoveProductOption(
                listingInfoToDelete.listingId,
                listingInfoToDelete.isBOD
            )
            setListingInfoToDelete(undefined)
            setIsRemoveWarningOpen(!isRemoveWarningOpen)
        }
    }

    const closeWarningDialog = () => {
        setListingInfoToDelete(undefined)
        setIsRemoveWarningOpen(!isRemoveWarningOpen)
    }

    const resetProductSearchQueryInput = (
        workspaceManufacturerSelectedState: WorkspaceManufacturerSelected
    ) => {
        setManufacturerProductSearchInput((past) => ({
            ...past,
            projectId,

            listingIdsList: workspaceManufacturerSelectedState.isSelected
                ? [
                    ...((past?.listingIdsList ?? []) as string[]),
                    workspaceManufacturerSelectedState.listingId,
                ]
                : past.listingIdsList?.filter(
                    (each) =>
                        each !== workspaceManufacturerSelectedState.listingId
                ),
            productTypeSelections: past.productTypeSelections?.filter(function (
                props
            ) {
                props.productTypeProperties = []
                return true
            }),
            orderBy: defaultListingOrder,
        }))
    }
    React.useEffect(() => {
        if (calculateEmptyListings()) {
            // do not query if there is no selected listing id and Added To Project Only is selected
            // and do not query if there is no flagged for review and Flagged for review is selected
            return
        }

        let includedListingIds: string[] = []
        const listingIdMap: Record<string, string[]> = {
            [productOptions.ADDED_TO_PROJECT]: productSelectedListingIds,
            [productOptions.FLAGGED_FOR_REVIEW]: flaggedForReviewListingIds,
        }

        Object.entries(product).map(([key, included]) => {
            if (!!included) {
                includedListingIds = [
                    ...includedListingIds,
                    ...listingIdMap[key],
                ]
            }
        })

        const productTypeSelections =
            manufacturerProductSearchInput.productTypeSelections

        setManufacturerProductSearchInput((past) => ({
            ...past,
            projectId,
            listingIdsList: includedListingIds,
            productTypeSelections: product[productOptions.ADDED_TO_PROJECT]
                ? productTypeSelections?.map((productTypeSelection) => {
                    return {
                        ...productTypeSelection,
                        productTypeProperties: [],
                    }
                })
                : productTypeSelections,
        }))
        setTabsFetched({ search: false, related: false })
        setRefetch(true)
        // NOTE: do NOT reset currentTotal here. doRefetch() calls setCurrentTotal(PER_PAGE)
        // only when a new network request actually fires (searchParamsChanged = true).
        // Resetting here causes the grid to shrink to PER_PAGE slots even when doRefetch
        // exits early (params unchanged), which strands all accumulated pagination pages
        // in mergedProducts and blocks handleLoadMore (mergedProducts.length !== currentTotal).
    }, [
        product,
        projectId,
        productSelectedListingIds,
        flaggedForReviewListingIds,
        calculateEmptyListings,
    ])

    React.useEffect(() => setHeaderVisibility(false), [setHeaderVisibility])

    React.useEffect(
        () => setProductsPage(location.pathname.includes('products')),
        [location]
    )
    const getMultiplePTsWithBOD = React.useCallback(() => {
        if (dialogTab === 0) {
            const selectedFamilyFilters = searchFacetInputs?.find(
                (item) => item.name === 'SectionNames'
            )?.values
            if (selectedFamilyFilters && selectedFamilyFilters.length !== 0) {
                const familyNodeIds = productTypeFacets
                    ?.filter((item) => {
                        return (
                            selectedFamilyFilters?.some(
                                (itemFamily) =>
                                    itemFamily === item?.parentProductFamily
                            ) && item?.familyNodeId
                        )
                    })
                    .map((item) => {
                        return item.familyNodeId || ''
                    })
                    .filter((item) => {
                        return item !== ''
                    })
                onGetProductTypesWithBod(
                    !isDivisionSelected && !!familyNodeIds?.length
                        ? Array.from(new Set(familyNodeIds))
                        : undefined
                )
            } else {
                onGetProductTypesWithBod(
                    isDivisionSelected || !nodeId ? undefined : [nodeId]
                )
            }
        }
    }, [
        productTypeFacets,
        searchFacetInputs,
        dialogTab,
        isAssembly,
        nodeId,
        getProductTypesWithBod,
        isForDivisionView,
        projectId,
        productTypeWithBODForDivisionInput,
        isDivisionSelected,
    ])

    React.useEffect(() => {
        const partnershipFilters = [...bimResources, ...sustainabiliyResources]

        setSearchFacetInputs((past) => {
            return [
                ...past.filter((each) => each.name !== 'partnerships'),
                {
                    name: 'partnerships',
                    values: partnershipFilters,
                },
            ]
        })
    }, [bimResources, sustainabiliyResources])

    // clear all of the advanced filters not the dropdowns
    const handleClearAllClick = useCallback(() => {
        setListingType([])
        setProduct(defaultProduct)
        setBIMResources([])
        setSustainabilityResources([])
        setSpecialty([])
        setSearchFacets([])
        setTabsFetched({ search: false, related: false })
        setRefetch(true)

        setManufacturerProductSearchInput(
            ({ listingIdsList, facets, ...past }) => ({
                facets: facets?.filter(
                    (facet) => facet.name !== 'withManufacturerOption'
                ),
                ...past,
            })
        )

        // Remove listingLevels entirely so the API applies no filter and returns all
        // listing levels (including premium). Setting values: [] would cause the API
        // to exclude premium products, same as a stale filter being left behind.
        setSearchFacetInputs((past) =>
            past.filter((each) => each.name !== 'listingLevels')
        )
    }, [defaultProduct, defaultManufacturerProductSearchInput])

    const removeProductType = React.useCallback(
        (value: string[]) => {
            let familyProductTypeList: string[] = []
            if (value && value.length > 0) {
                value.forEach((val) => {
                    const tempList = productTypeFacets
                        .filter((each) => each.parentProductFamily === val)
                        ?.flatMap((ele) => ele.value ?? '')
                    const filteredVal = tempList?.filter(
                        (each) => each && each !== ''
                    )
                    if (filteredVal && filteredVal.length > 0) {
                        familyProductTypeList = [
                            ...familyProductTypeList,
                            ...filteredVal,
                        ]
                    }
                })
            }
            const existingProductTypeList = searchFacetInputs.find(
                (each) => each.name === 'ProductType'
            )
            const productTypeList = existingProductTypeList?.values.filter(
                (n) => {
                    return familyProductTypeList.indexOf(n) !== -1
                }
            )
            handleFacetSelectedChange('SectionNames', value)
            handleFacetSelectedChange('ProductType', productTypeList ?? [])
        },
        [productTypeFacets, handleFacetSelectedChange, searchFacetInputs]
    )

    const getBreadcrumbsWithCategory = React.useCallback((): string => {
        return category && assemblyPath
            ? `${category} / ${assemblyPath}`
            : category ?? ''
    }, [category, assemblyPath])

    const updateIsBannerExpanded = React.useCallback(
        (value: boolean) => {
            setIsBannerExpanded(value)
        },
        [setIsBannerExpanded]
    )

    React.useEffect(() => {
        if (projectId && selectedProjectNode) {
            getSelectedProducts()
        }
    }, [projectId, selectedProjectNode, getSelectedProducts])

    React.useEffect(() => {
        if (productsPage) {
            setSearchFacetInputs((past) => [
                ...past.filter((each) => each.name !== 'submissionStatus'),
            ])
            setOrderBy('lastUpdated desc')
        }
    }, [productsPage])

    React.useEffect(() => {
        if (!autosuggestLoading && autosuggestData && !autosuggestSaved) {
            setSearchFacets((past) => [
                ...past.filter((each) => each.name !== autosuggestFacet.name),
                {
                    name: autosuggestFacet.name,
                    values:
                        autosuggestData.listingAutoSuggest.result?.map(
                            (each) => ({
                                value: each,
                            })
                        ) ?? [],
                },
            ])
            setAutosuggestSaved(true)
        }
    }, [
        autosuggestData,
        autosuggestLoading,
        autosuggestFacet.name,
        autosuggestSaved,
    ])

    React.useEffect(() => {
        if (
            !autosuggestCardLoading &&
            autosuggestCardData &&
            !autosuggestCardSaved
        ) {
            if (autosuggestFacet?.name === 'ProductType') {
                setSearchFacets((past) => [
                    ...past.filter(
                        (each) =>
                            each.name?.toLowerCase() !==
                            autosuggestFacet?.name?.toLowerCase()
                    ),
                    {
                        name: 'ProductType',
                        values: autosuggestCardData?.listingCardsAutoSuggest?.productFamilyToProductTypes?.map(
                            (each) => ({
                                value: each?.productType ?? '',
                                parentProductFamily:
                                    each?.parentProductFamily ?? '',
                                specAgentProductTypeId:
                                    each?.specAgentProductTypeId ?? '',
                            })
                        ),
                    },
                ])
                if (isProductTypeFacetsLoading) {
                    const updatedPTFacets =
                        autosuggestCardData?.listingCardsAutoSuggest
                            ?.productFamilyToProductTypes
                    const ptNames = searchFacetInputs.find(
                        (i) => i.name === 'ProductType'
                    )
                    const filteredUpdatedPTFacets =
                        updatedPTFacets?.filter((item) =>
                            ptNames?.values.some(
                                (each) => each === item.productType
                            )
                        ) ?? []
                    const newSelectedPTFacets = filteredUpdatedPTFacets.map(
                        (item) => {
                            return {
                                parentProductFamily: item.parentProductFamily,
                                pTNodeId: item.pTNodeId,
                                pTElementId: item.pTElementId,
                                familyElementId: item.familyElementId,
                                familyNodeId: item.familyNodeId,
                                specAgentProductTypeId:
                                    item.specAgentProductTypeId,
                                value: item.productType,
                            } as Partial<SearchFacets>
                        }
                    )
                    setProductTypeFacets((past) =>
                        removeDuplicateProductTypeFacets([
                            ...newSelectedPTFacets,
                            ...past,
                        ] as Partial<SearchFacets>[])
                    )
                    setIsProductTypeFacetsLoading(false)
                }
            }
            if (autosuggestFacet?.name === 'SectionNames') {
                setSearchFacets((past) => [
                    ...past.filter(
                        (each) =>
                            each.name?.toLowerCase() !==
                            autosuggestFacet?.name?.toLowerCase()
                    ),
                    {
                        name: 'SectionNames',
                        values: [
                            ...new Set(
                                autosuggestCardData?.listingCardsAutoSuggest?.productFamilyToProductTypes?.map(
                                    (each) => each?.parentProductFamily ?? ''
                                ) ?? []
                            ),
                        ].map((family) => ({
                            value: family,
                        })),
                    },
                ])
                if (isProductTypeFacetsLoading) {
                    const updatedPTFacets =
                        autosuggestCardData?.listingCardsAutoSuggest
                            ?.productFamilyToProductTypes
                    const familyNames = searchFacetInputs.find(
                        (i) => i.name === 'SectionNames'
                    )
                    const filteredUpdatedPTFacets =
                        updatedPTFacets?.filter((item) =>
                            familyNames?.values.some(
                                (each) => each === item.parentProductFamily
                            )
                        ) ?? []
                    const newSelectedPTFacets = filteredUpdatedPTFacets.map(
                        (item) => {
                            return {
                                parentProductFamily: item.parentProductFamily,
                                pTNodeId: item.pTNodeId,
                                pTElementId: item.pTElementId,
                                familyElementId: item.familyElementId,
                                familyNodeId: item.familyNodeId,
                                specAgentProductTypeId:
                                    item.specAgentProductTypeId,
                                value: item.productType,
                            } as Partial<SearchFacets>
                        }
                    )
                    setProductTypeFacets((past) =>
                        removeDuplicateProductTypeFacets([
                            ...newSelectedPTFacets,
                            ...past,
                        ])
                    )
                    fetchAutoSuggest('ProductType', '')
                }
            }
            setAutosuggestCardSaved(true)
        }
    }, [
        autosuggestCardData,
        autosuggestCardLoading,
        autosuggestFacet.name,
        autosuggestCardSaved,
        isProductTypeFacetsLoading,
        searchFacetInputs,
    ])

    // Used to reset the scrollbar
    React.useEffect(() => {
        if (previousDialogTab === dialogTab) return
        if (hasMountedRef.current) {
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    const grid = list._listRef as FixedSizeGrid
                    list.resetloadMoreItemsCache() //Reset infinite scroll
                    grid.scrollToItem({
                        align: 'start',
                        columnIndex: 0,
                        rowIndex: 0,
                    }) //scroll to top after refresh
                    list.scrollTop = 0 //firefox solution for scroll not reseting
                }
            }
        }
        hasMountedRef.current = true
    }, [
        refetch,
        searchLoading,
        dialogTab,
        searchFetchMoreLoading,
        getListingsLoading,
    ])

    // Used to trigger rescroll after adding/removing listing
    React.useEffect(() => {
        // Skip on initial mount - only scroll when explicitly needed (after add/remove)
        if (!hasMountedRef.current) {
            return
        }

        if (!searchLoading && !refetch && !searchFetchMoreLoading) {
            const selectedListingId = getSelectedListingId()
            const count = mergedProducts?.length
            if (selectedListingId && count) {
                const index =
                    mergedProducts?.findIndex(
                        (each) => each.listingId === selectedListingId
                    ) ?? -1
                if (index > -1 && isItemLoaded(index)) {
                    // Found the selected listing, scroll to it
                    const row = Math.ceil((index + 1) / columnCount) - 1
                    const col = index % columnCount
                    setScrollRowAndColumn(row, col)
                    setReScroll(true)
                    updateSelectedListingId('')
                } else {
                    // Not found - just clear the selected ID without scrolling
                    // This prevents auto-scroll to bottom on initial load
                    updateSelectedListingId('')
                }
            }
        }
    }, [
        mergedProducts,
        searchLoading,
        refetch,
        searchFetchMoreLoading,
    ])

    // Used to rescroll to the selected product
    React.useEffect(() => {
        if (reScroll)
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    list._listRef.scrollToItem({
                        align: 'start',
                        rowIndex: scrollState.rowIndex,
                        columnIndex: scrollState.columnIndex,
                    })
                    setReScroll(false)
                }
            }
    }, [scrollState, reScroll])

    const updateProductTypeFacets = React.useCallback(
        (facet: ProductListingDialogFacet) => {
            setIsProductTypeFacetsLoading(true)
            fetchAutoSuggest(facet, '')
        },
        [searchFacetInputs, fetchAutoSuggest]
    )

    // For PRODUCTTYPE/PRODUCTFAMILY elements, isProductTypeFacetsLoading starts
    // as true and must be resolved by completing the autosuggest chain once per node.
    // For all other element types (ASSEMBLY, etc.), immediately clear the loading flag so
    // filters are not stuck disabled — those nodes don't need the autosuggest chain.
    React.useEffect(() => {
        if (!productsPage && (isProductType || isProductFamily)) {
            updateProductTypeFacets('SectionNames')
        } else if (!productsPage) {
            setIsProductTypeFacetsLoading(false)
        }
        // nodeId is the only trigger — runs on mount and when navigating to a new element.
        // eslint-disable-next-line
    }, [nodeId])

    // Handles dialog update when a different node was clicked
    React.useMemo(() => {
        if (productsPage) return

        if (
            !!isAEAllProductsToggled &&
            !!previousNodeId &&
            previousNodeId !== nodeId
        ) {
            // Return to non all products when selecting different node
            setHeaderPillToggle(false)
        }

        setCloseSlide(true)
        // Only reset filter state when switching to a different node (user selected a different element).
        // If nodeId is unchanged (elementId updated post-mutation via SW message), preserve the
        // user's active PT filters — otherwise they reset after every add-to-project action.
        if (!previousNodeId || previousNodeId !== nodeId) {
            setProductTypeFacets(defaultProductTypeFacets)
            setListingType([])
            setSearchFacetInputs((past) => {
                let filtered = [
                    ...past.filter(
                        (each) =>
                            each.name !== 'ProductType' &&
                            each.name !== 'SectionNames' &&
                            each.name !== 'categories' &&
                            each.name !== 'assemblyNodeId' &&
                            each.name !== 'listingLevels'
                    ),
                ]
                if (hasProductType) {
                    filtered = [
                        ...filtered,
                        {
                            name: 'ProductType',
                            values: [productType as string],
                        },
                    ]
                }

                if (hasFamily) {
                    filtered = [
                        ...filtered,
                        {
                            name: 'SectionNames',
                            values: [family as string],
                        },
                    ]
                }

                if (category && category !== 'All Categories') {
                    filtered = [
                        ...filtered,
                        {
                            name: 'categories',
                            values: [category],
                        },
                    ]
                }
                if (
                    !isProductFamily &&
                    !isProductType &&
                    (isAssembly || isLastSubAssembly)
                ) {
                    filtered = [
                        ...filtered,
                        { name: 'assemblyNodeId', values: [nodeId as string] },
                    ]
                }
                return [...filtered]
            })
        }
    }, [
        family,
        hasFamily,
        category,
        productsPage,
        hasProductType,
        productType,
        isProductFamily,
        isProductType,
        isAssembly,
        isLastSubAssembly,
        nodeId,
        isAEAllProductsToggled,
        elementId,
        previousNodeId,
    ])

    const selectedProductTypesWithManufacturerOption = React.useMemo(() => {
        return productSelected
            ?.filter(
                (selectedProduct: ProductListingSelected) =>
                    !!selectedProduct?.hasSupportedManufacturersOptions
            )
            ?.map((each) => each?.productType?.toUpperCase())
    }, [productSelected])

    React.useMemo(() => {
        if (productsPage) return

        let localStoragePtpOptions:
            | ProductTypePropertiesInputType[]
            | undefined = undefined

        if (!Object.values(product).some((value) => !!value))
            localStoragePtpOptions = getFromLocalStorage(
                userId!,
                projectId!,
                'propertyOption'
            ) as ProductTypePropertiesInputType[]

        setProductTypeSelections(
            defaultProductTypeSelections(
                hasFamily,
                family as string,
                hasProductType,
                productType as string,
                localStoragePtpOptions ?? undefined
            )
        )

        setOrderBy(defaultListingOrder)
    }, [
        userId,
        projectId,
        family,
        hasFamily,
        productsPage,
        hasProductType,
        productType,
        product,
        isAssembly,
        isLastSubAssembly,
    ])
    // TODO: Refector code to change the input
    // For now, update to this hook should be done one at a time
    React.useMemo(() => {
        if (!orderBy) return

        let isAddedToProjectOnly = product[productOptions.ADDED_TO_PROJECT]
        const ptps = isAddedToProjectOnly
            ? productTypeSelections?.map((productTypeSelection) => {
                return {
                    ...productTypeSelection,
                    productTypeProperties: [],
                }
            })
            : productTypeSelections

        //get selected inputs in product type filter
        const selectedProductTypeInputs = searchFacetInputs.filter(
            (item) => item.name === 'ProductType'
        )?.[0]?.values
        const selectedFamilyInputs = searchFacetInputs.filter(
            (item) => item.name === 'SectionNames'
        )?.[0]?.values
        //get selected product type name in workspace

        let updatedSearchFacetInputs = [...searchFacetInputs]

        // In case of Project, we have to pass specAgentProductTypeId of ProductTypes
        if (nodeId && projectId) {
            updatedSearchFacetInputs = replaceSpecAgentIdForProductTypes(
                updatedSearchFacetInputs,
                productTypeFacets
            )
        }

        // get Product Type Selections Data for comparing productTypeFacets and searchFacetInputs
        const productTypeSelectionsData = getProductTypeSelectionsData(
            productTypeFacets,
            searchFacetInputs
        )

        setManufacturerProductSearchInput((past) => ({
            ...past,
            orderBy: defaultListingOrder,
            projectId,

            facets: ptps?.some((ptp) => ptp.productTypeProperties?.length! > 0)
                ? [...updatedSearchFacetInputs]
                : updatedSearchFacetInputs,
            productTypeSelections: productTypeSelectionsData
                ? productTypeSelectionsData?.map((item, index) => {
                    const withManufacturerOptionFilter = selectedProductTypesWithManufacturerOption?.includes(
                        item.value?.toUpperCase()
                    )
                        ? { withManufacturerOption: true }
                        : {}

                    return {
                        sectionName: item.parentProductFamily,
                        productTypeName: item.value,
                        productTypeProperties: ptps?.find(
                            (ptp) =>
                                ptp?.sectionName ===
                                selectedFamilyInputs?.[index]
                        )?.productTypeProperties,
                        ...withManufacturerOptionFilter,
                    }
                })
                : ptps,
        }))
        //check if the ptp is changed
        if (
            !isEqual(
                productTypeSelections?.flatMap(
                    (x) => x.productTypeProperties ?? []
                ),
                propertyOptions
            )
        ) {
            setTabsFetched({ search: false, related: false })
            setRefetch(true) // Removing this code affects the live refresh on product listing when ptp changes
        }
    }, [
        searchFacetInputs,
        propertyOptions,
        productTypeSelections,
        orderBy,
        selectedProductTypesWithManufacturerOption,
        productTypeFacets,
    ])

    const handleProductAddFlagForReview = React.useCallback(
        (listingId: string, isBOD: boolean) => {
            if (!!projectId) {
                // Flag the product for review
                setIsFlagForReviewInProgress(true)
                addFlagForReview({
                    variables: {
                        projectId: projectId,
                        listingId: listingId,
                        isFromRevit,
                    },
                }).then((value) => {
                    setIsFlagForReviewInProgress(false)

                    // Remove the product from the list only if already added
                    const addedListing = productSelected?.find(
                        (product) => product?.listingId === listingId
                    )
                    if (!!addedListing) {
                        handleRemoveProductOption(listingId, isBOD)
                    } else {
                        setProductListingFlaggedForReview(
                            value?.data?.createProductListingFlagForReview
                                ?.items || []
                        )
                    }
                })
            }
        },
        [
            projectId,
            addFlagForReview,
            setProductListingFlaggedForReview,
            setIsFlagForReviewInProgress,
            handleRemoveProductOption,
            productSelected,
        ]
    )

    const handleProductRemoveFlagForReview = React.useCallback(
        (listingId: string) => {
            if (!!projectId) {
                setIsFlagForReviewInProgress(true)
                removeFlagForReview({
                    variables: {
                        projectId: projectId,
                        listingId: listingId,
                    },
                }).then((value) => {
                    setProductListingFlaggedForReview(
                        value?.data
                            ?.removeProductListingFlagForReviewByListingId
                            ?.items || []
                    )
                    setIsFlagForReviewInProgress(false)
                    if (product[productOptions.FLAGGED_FOR_REVIEW]) {
                        setManufacturerProductSearchInput((past) => ({
                            ...past,
                            projectId,
                            listingIdsList: past.listingIdsList?.filter(
                                (each) => each !== listingId
                            ),
                        }))
                        setTabsFetched({ search: false, related: false })
                        setRefetch(true)
                    }
                })
            }
        },
        [
            projectId,
            removeFlagForReview,
            setProductListingFlaggedForReview,
            setIsFlagForReviewInProgress,
            product,
        ]
    )

    //refetch on filter change
    React.useEffect(() => {
        // Reset tab fetch state so both tabs refetch with new filters
        setTabsFetched({ search: false, related: false })
        // Reset last params to force refetch with new filters
        lastSearchParamsRef.current = null
        lastRelatedParamsRef.current = null
        setRefetch(true)
    }, [searchFacetInputs])

    // on refetch change
    React.useEffect(() => {
        if (!searchLoading && refetch) {
            doRefetch()
        }
    }, [doRefetch, searchLoading, refetch])

    // on Render — fallback for when doRefetch wasn't ready at mount.
    // initialFetchFiredRef prevents this from looping when searchCalled stays false
    // permanently (all items served from Zustand cache, Phase 2 never fires).
    React.useEffect(() => {
        if (!searchCalled && hasMountedRef.current && !initialFetchFiredRef.current) {
            initialFetchFiredRef.current = true
            doRefetch()
        }
    }, [doRefetch, searchCalled])

    React.useEffect(() => {
        if (
            !searchLoading &&
            !getListingsLoading &&
            !searchFetchMoreLoading &&
            !!mergedProducts &&
            !!productsCount
        ) {
            const count = mergedProducts?.length

            // Functional update avoids stale closure — no need to add currentTotal to deps
            setCurrentTotal((prev) => (prev !== count ? count : prev))
            // Always update hasNextPage — it must reflect current productsCount regardless
            // of whether currentTotal changed (e.g. after doRefetch resets currentTotal to PER_PAGE)
            setHasNextPage(count >= 50 ? count < productsCount : false)
        }
        if (
            !searchLoading &&
            !getrelatedProductsLoading &&
            !searchFetchMoreLoading &&
            !!mergedRelatedProducts &&
            !!relatedProductsCount
        ) {
            const count = mergedRelatedProducts?.length

            // Functional update avoids stale closure — no need to add currentRTotal to deps
            setCurrentRTotal((prev) => (prev !== count ? count : prev))
            // Always update rHasNextPage — same reasoning as above
            setRHasNextPage(count >= 50 ? count < relatedProductsCount : false)
        }
    }, [
        searchLoading,
        mergedProducts,
        productsCount,
        getListingsLoading,
        searchFetchMoreLoading,
        mergedRelatedProducts,
        relatedProductsCount,
        getrelatedProductsLoading,
    ])

    React.useEffect(() => {
        if (
            !projectCalled &&
            !projectLoading &&
            projectId &&
            projectId !== '' &&
            projectId !== 'undefined'
        ) {
            getProject({
                variables: {
                    projectId: projectId,
                },
            })
        }
    }, [getProject, projectCalled, projectLoading, projectId])

    React.useEffect(() => {
        if (!autosuggestCardData) {
            if (nodeId && nodeId !== 'undefined') {
                if (!isAssembly) {
                    onGetProductTypesWithBod(
                        isDivisionSelected || !nodeId ? undefined : [nodeId]
                    )
                }
            }
            if (isGettingSelectedNuggetRequired) {
                getSelectedNugget({
                    variables: {
                        input: {
                            elementId,
                            projectId,
                        },
                        nodeId: nodeId,
                    },
                })
            }
        }
    }, [
        elementType,
        elementId,
        nodeId,
        projectId,
        isGettingSelectedNuggetRequired,
        getProductTypesWithBod,
        getSelectedNugget,
        isAssembly,
        isProductType,
        productTypeWithBODForDivisionInput,
        isDivisionSelected,
    ])

    // Read data from service worker
    React.useEffect(() => {
        if (!serviceWorker) return

        const handleServiceWorkerMessage = ({ data }: MessageEvent) => {
            // make sure that the data coming from SW is for this page.
            const workspaceProductListingState = data[
                WORKSPACE_PRODUCT_LISTING_KEY
            ] as WorkspaceProductListing
            if (workspaceProductListingState !== undefined) {
                // check if same project id is being serviced
                if (workspaceProductListingState.projectId !== projectId) return

                // Update with new values from workspace, fallback to current URL values
                const preservedState = {
                    ...workspaceProductListingState,
                    baseElementId:
                        workspaceProductListingState.baseElementId ||
                        baseElementId ||
                        '',
                    treePath:
                        workspaceProductListingState.treePath || treePath || '',
                }

                history.push(getDialogUrl(preservedState, 'dialog'))
                setCloseSlide(false)

                // Update filters when element changes from tree click
                setSearchFacetInputs((past) => {
                    let filtered = [
                        ...past.filter(
                            (each) =>
                                each.name !== 'ProductType' &&
                                each.name !== 'SectionNames' &&
                                each.name !== 'categories' &&
                                each.name !== 'assemblyNodeId'
                        ),
                    ]

                    // Add ProductType filter if present
                    if (
                        workspaceProductListingState.productType &&
                        workspaceProductListingState.productType !== '' &&
                        workspaceProductListingState.productType !== 'undefined'
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'ProductType',
                                values: [
                                    workspaceProductListingState.productType,
                                ],
                            },
                        ]
                    }

                    // Add Family filter if present
                    if (
                        workspaceProductListingState.family &&
                        workspaceProductListingState.family !== '' &&
                        workspaceProductListingState.family !== 'undefined'
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'SectionNames',
                                values: [workspaceProductListingState.family],
                            },
                        ]
                    }

                    // Add category filter if present
                    if (
                        workspaceProductListingState?.category?.name &&
                        workspaceProductListingState.category.name !==
                        'All Categories'
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'categories',
                                values: [
                                    workspaceProductListingState.category.name,
                                ],
                            },
                        ]
                    }

                    // Add assemblyNodeId filter if it's an assembly
                    const isAssemblyElement =
                        workspaceProductListingState.elementType ===
                        'ASSEMBLY' &&
                        !workspaceProductListingState.isLastSubAssembly
                    const isProductFamilyElement =
                        workspaceProductListingState.elementType ===
                        'PRODUCTFAMILY'
                    const isProductTypeElement =
                        workspaceProductListingState.elementType ===
                        'PRODUCTTYPE'

                    if (
                        !isProductFamilyElement &&
                        !isProductTypeElement &&
                        (isAssemblyElement ||
                            workspaceProductListingState.isLastSubAssembly) &&
                        workspaceProductListingState.nodeId
                    ) {
                        filtered = [
                            ...filtered,
                            {
                                name: 'assemblyNodeId',
                                values: [workspaceProductListingState.nodeId],
                            },
                        ]
                    }

                    return [...filtered]
                })

                if (
                    workspaceProductListingState?.updateBODList &&
                    workspaceProductListingState?.specAgentIdPt !==
                    specAgentIdPt &&
                    nodeId
                ) {
                    onGetProductTypesWithBod(
                        isDivisionSelected || !nodeId ? undefined : [nodeId]
                    )
                }
            }

            const workspaceManufacturerSelectedState = data[
                WORKSPACE_MANUFACTURER_SELECTED_KEY
            ] as WorkspaceManufacturerSelected

            if (workspaceManufacturerSelectedState !== undefined) {
                if (workspaceManufacturerSelectedState.removeSelected) {
                    setProductSelected([])
                    resetProductSearchQueryInput(
                        workspaceManufacturerSelectedState
                    )
                } else {
                    if (workspaceManufacturerSelectedState.isSelected) {
                        setProductSelected((past) => [
                            ...past.filter(
                                (each) =>
                                    each.listingId !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                            {
                                listingId:
                                    workspaceManufacturerSelectedState.listingId,
                                selectionType: workspaceManufacturerSelectedState.isBOD
                                    ? 'BOD'
                                    : 'Product',
                                isBOD:
                                    workspaceManufacturerSelectedState.isBOD ??
                                    false,
                                hasSupportedManufacturersOptions:
                                    workspaceManufacturerSelectedState.hasSupportedManufacturersOptions ??
                                    false,
                                elementId: workspaceManufacturerSelectedState.elementId as string,
                            },
                        ])
                        setManufacturerProductSearchInput((past) => ({
                            ...past,
                            projectId,
                            listingIdsList: past.listingIdsList?.filter(
                                (each) =>
                                    each !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                        }))
                    } else {
                        setProductSelected((past) => [
                            ...past.filter(
                                (each) =>
                                    each.listingId !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                        ])
                    }
                }

                if (workspaceManufacturerSelectedState.deleteBODListing) {
                    const matchedListing: ProductListingSelected = productSelected.filter(
                        (item) => item.isActive && item.selectionType === 'BOD'
                    )?.[0]
                    if (matchedListing) {
                        // Get productFamilyId from projectNuggetData (most reliable source)
                        const nuggetProductFamilyId =
                            projectNuggetData?.getProjectNugget
                                ?.projectProductTypePropertiesView
                                ?.productFamilyId

                        deleteListingMutation({
                            isBOD: true,
                            listingId: matchedListing.listingId,
                            productFamilyId: nuggetProductFamilyId || '',
                            projectId,
                            projectNuggetId: matchedListing.elementId || undefined,
                            isRetainProperties: false, // TODO: Change value if there is removal warning
                            firmName: matchedListing?.firmName,
                            type: matchedListing?.type
                                ?.replace(/\s/g, '')
                                .toUpperCase(),
                        } as DeleteProjectManufacturerProductListingInput)
                        setProductSelected((past) => [
                            ...past.filter(
                                (each) =>
                                    each.listingId !== matchedListing.listingId
                            ),
                        ])
                    }

                    resetProductSearchQueryInput(
                        workspaceManufacturerSelectedState
                    )
                }
                if (!!product[productOptions.ADDED_TO_PROJECT]) {
                    setManufacturerProductSearchInput((past) => ({
                        ...past,
                        projectId,

                        listingIdsList: workspaceManufacturerSelectedState.isSelected
                            ? [
                                ...((past?.listingIdsList ?? []) as string[]),
                                workspaceManufacturerSelectedState.listingId,
                            ]
                            : past.listingIdsList?.filter(
                                (each) =>
                                    each !==
                                    workspaceManufacturerSelectedState.listingId
                            ),
                    }))
                    setCurrentTotal(OPTIMISTIC_CARDS)
                }
                // Get updated list for listings that are flagged for review
                getProductFlaggedForReviewProducts({
                    variables: {
                        projectId: projectId,
                    },
                })
                if (isGettingSelectedNuggetRequired) {
                    getSelectedNugget({
                        variables: {
                            input: {
                                elementId: isProductType ? elementId : '',
                                projectId,
                            },
                            nodeId: nodeId,
                        },
                    })
                }
                if (
                    !isAssembly &&
                    nodeId &&
                    typeof nodeId !== 'undefined' &&
                    nodeId !== 'undefined'
                ) {
                    onGetProductTypesWithBod(
                        isDivisionSelected || !nodeId ? undefined : [nodeId]
                    )
                }
                setTabsFetched({ search: false, related: false })
                setRefetch(true)
            }

            const workspaceEnabledPTPOptions = data[
                WORKSPACE_ENABLED_PTP_OPTIONS
            ] as ProductTypePropertiesInputType[]

            if (
                workspaceEnabledPTPOptions &&
                !Object.values(product).some((value) => !!value)
            ) {
                // delay update to input since this will be updated many times in the workspace
                debounceUpdate(
                    workspaceEnabledPTPOptions.map((ptpOption) => ({
                        propertyName: ptpOption.propertyName.toString(),
                        propertyOptionIds: ptpOption.propertyOptionIds.map(
                            (property) => property.toString().normalize('NFKC')
                        ),
                    }))
                )
            }
            // check to see if BP tags are updated on the project workspace page
            const workspaceProductTypeManufacturerListingItems =
                data[WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]
            if (!!workspaceProductTypeManufacturerListingItems) {
                handleUpdateWorkspaceManufacturerBP(
                    workspaceProductTypeManufacturerListingItems
                )
            }
        }

        serviceWorker?.addEventListener('message', handleServiceWorkerMessage)
        return () => {
            serviceWorker?.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
        }
    }, [
        serviceWorker,
        history,
        projectId,
        product,
        hasFamily,
        family,
        hasProductType,
        productType,
        productSelected,
        elementType,
        elementId,
        projectNuggetData,
        isGettingSelectedNuggetRequired,
        debounceUpdate,
        deleteListingMutation,
        getProductTypesWithBod,
        getSelectedNugget,
        isAssembly,
        isProductType,
        nodeId,
        isProductFamily,
        getProductTypeFacetFromListing,
        productTypeWithBODForDivisionInput,
        isDivisionSelected,
        getProductFlaggedForReviewProducts,
        getProjectNuggetIdAndFamilyIdFromFacets,
        productTypeFacets,
    ])

    React.useEffect(() => {
        window.addEventListener('beforeunload', unloadListener)

        return () => {
            window.removeEventListener('beforeunload', unloadListener)
        }
    }, [unloadListener])

    React.useEffect(() => {
        updateBrowserTitle?.(
            projectData?.getProject?.name
                ? `${projectData?.getProject?.name} - ${ProductListings}`
                : `${ProductListings}`
        )
    }, [projectData, updateBrowserTitle])

    React.useMemo(() => {
        if (projectId && !productsPage) {
            // set in localstorage if window is open
            updatePersistentConfigs(
                userId,
                projectId,
                'projectProductDialogOpened',
                true
            )
        }
    }, [projectId, userId, productsPage])

    React.useMemo(() => {
        const loadedIds =
            projectProductListingData?.getProjectManufacturerProductListing.map(
                (each) => each.listingId as string
            ) ?? []

        if (!projectProductListingLoading && wasListingsLoading) {
            if (!isEqual(productSelectedListingIds, loadedIds)) {
                setProductSelected(
                    projectProductListingData?.getProjectManufacturerProductListing.map(
                        (each) => ({
                            listingId: each.listingId as string,
                            selectionType: each.isBOD ? 'BOD' : 'Product',
                            isBOD: each.isBOD ?? false,
                            hasSupportedManufacturersOptions:
                                each.hasSupportedManufacturersOptions ?? false,
                            productType: each.productType,
                            elementId: each.elementId as string,
                            firmName: each.firmName as string,
                            type: each.type as ListingLevel,
                        })
                    ) ?? []
                )
            }
        }
    }, [
        projectProductListingData,
        projectProductListingLoading,
        productSelectedListingIds,
        wasListingsLoading,
    ])

    React.useEffect(() => {
        if (
            currentUserInfoResponse &&
            JSON.stringify(userDetails) === JSON.stringify(userInfoInitial)
        ) {
            setUser(currentUserInfoResponse.currentUserInfo)
        }
    }, [userDetails, currentUserInfoResponse])

    if (!currentUserInfoResponse) return null

    return (
        <>
            <DialogProvider
                getFacetSelectedOptions={getFacetSelectedOptions}
                projectDetail={projectData?.getProject}
                hidePopper={
                    !!productsPage ||
                    // !!divisionNumber || NOTE: commented in lieu of #2413754
                    !!isAEAllProductsToggled
                }
                closeSlide={closeSlide}
                productSelected={productSelected}
                handleAddProductOption={handleAddProductOption}
                handleRemoveProductOption={onRemoveProductListing}
                handleModifyProductOption={handleModifyProductOption}
                selectedManufacturers={selectedManufacturers}
                handleToggleMultipleManufacturerSelections={
                    handleToggleMultipleManufacturerSelections
                }
                handleMultiSelectManufacturer={handleMultiSelectManufacturer}
                handleSelectUnselectAllManufacturers={
                    handleSelectUnselectAllManufacturers
                }
                productListingFlaggedForReview={productListingFlaggedForReview}
                handleProductAddFlagForReview={handleProductAddFlagForReview}
                handleProductRemoveFlagForReview={
                    handleProductRemoveFlagForReview
                }
                handleUpdateWorkspaceManufacturerBP={
                    handleUpdateWorkspaceManufacturerBP
                }
                emptyElementTree={
                    assemblyPath === null ||
                    assemblyPath === undefined ||
                    assemblyPath?.trim()?.length === 0
                }
                userRoles={currentUserInfoResponse?.currentUserInfo.roles}
                selectedListingId={listingId as string}
                projectProductListings={
                    isProductType && !projectNUggetLoading
                        ? projectNuggetData?.getProjectNugget.productListings
                        : undefined
                }
                isAEAllProductsToggled={isAEAllProductsToggled}
                isProductsPage={productsPage}
                setHeaderPillToggle={setHeaderPillToggle}
                searchListings={mergedProducts}
                productSelectedListingIds={productSelectedListingIds}
                mainTabIndex={dialogTab}
                isInProject={isInProject}
                listingType={listingType}
                selectedProductType={productType ?? undefined}
                productTypesInProject={projectPTNames}
            >
                <DialogAppBar name="Product Listings" />
                <DialogContent
                    isBannerExpanded={isBannerExpanded}
                    slideContent={
                        <SlideContent
                            searchLoading={
                                searchLoading || searchFetchMoreLoading
                            }
                            projectId={projectId}
                        />
                    }
                >
                    <DialogFilterProvider
                        getFacetOptions={getFacetOptions}
                        getProductFacetOptions={getProductFacetOptions}
                        handleFacetSelectedChange={handleFacetSelectedChange}
                        removeProductType={removeProductType}
                        isProductTypeFacetsLoading={isProductTypeFacetsLoading}
                    >
                        <DialogHeader
                            category={getBreadcrumbsWithCategory()}
                            productTypeFacets={productTypeFacets}
                            handleAutocompleteNameChange={
                                handleAutocompleteNameChange
                            }
                            handleAutocompleteInputValueChange={
                                handleAutocompleteInputValueChange
                            }
                            autosuggestLoading={
                                autosuggestLoading ||
                                getListingsLoading ||
                                searchFetchMoreLoading ||
                                searchLoading ||
                                autosuggestCardLoading
                            }
                            queryParams={queryParams}
                        />
                        <AdvancedFilters
                            listingType={listingType}
                            listingTypeOptions={
                                productsPage
                                    ? defaultFilter
                                    : [...defaultFilter, 'Manufacturer']
                            }
                            handleListingType={handleListingType}
                            product={product}
                            productOptions={Object.values(productOptions)}
                            handleProduct={handleProduct}
                            handleAutocompleteNameChange={
                                handleAutocompleteNameChange
                            }
                            autosuggestLoading={
                                autosuggestLoading ||
                                getListingsLoading ||
                                searchFetchMoreLoading ||
                                searchLoading ||
                                autosuggestCardLoading
                            }
                            handlePartnershipSelection={
                                handlePartershipSelection
                            }
                            bimResources={bimResources}
                            sustainabilityResources={sustainabiliyResources}
                            sustainabilityResourcesOptions={
                                sustainabilityResourcesOptions
                            }
                            bimResourcesOptions={bimResourcesOptions}
                            queryParams={queryParams}
                            selectedTab={dialogTab}
                            specialty={specialty}
                            handleSpecialtyClick={handleSpecialtyClick}
                            specialtyOptions={['Manufacturer Options']}
                            handleClearAllClick={handleClearAllClick}
                        />
                    </DialogFilterProvider>
                    <DialogTabs
                        listingProps={{
                            searchLoading: searchLoading,
                            searchFetchMoreLoading: searchFetchMoreLoading,
                            handleLoadMore: handleLoadMore,
                            products: isEmptyListingDueToFilter
                                ? [] // Show no Results
                                : mergedProducts,
                            currentTotal: currentTotal,
                            hasNextPage: hasNextPage,
                            isItemLoaded: isItemLoaded,
                            scrollState: scrollState,
                            setScrollRowAndColumn: setScrollRowAndColumn,
                            infiniteLoaderRef: infiniteLoaderRef,
                            setColumnCount: setColumnCount,
                            isAssembly: isAssembly,
                            productTypesWithBOD: productTypesWithBOD,
                        }}
                        rListingProps={{
                            products: isEmptyListingDueToFilter
                                ? [] // Show no Results
                                : mergedRelatedProducts,
                            currentTotal: currentRTotal,
                            hasNextPage: rHasNextPage,
                        }}
                        setDialogTab={setDialogTab}
                        relatedProductsCount={
                            isEmptyListingDueToFilter
                                ? 0
                                : relatedProductsCount
                        }
                        productsCount={productsCount}
                        isRelatedProductsActive={dialogTab === 1}
                    />
                </DialogContent>
                {userId !== undefined &&
                    processEnv.REACT_APP_EnablePPLFeature !== '1' && (
                        <RelatedProductsBanner
                            category={category ?? ''}
                            setDialogTab={setDialogTab}
                            activeDialogTab={dialogTab}
                            updateDialogContainer={updateIsBannerExpanded}
                            userId={userId}
                        />
                    )}
                <Backdrop
                    className={classes.backdrop}
                    open={isAddElementsLoading || isFlagForReviewInProgress}
                >
                    <CircularProgress
                        color="inherit"
                        data-testid="addSpinner"
                    />
                </Backdrop>
                {/* Unmapped product dialog - rendered at top level to prevent virtualized list unmounting issues */}
                <UnmappedProductDialogWrapper projectId={projectId} />
            </DialogProvider>
            {isRemoveWarningOpen && (
                <RemoveProductWarning
                    isDisplayed={isRemoveWarningOpen}
                    onCancel={closeWarningDialog}
                    onRemoveProductListing={triggerRemoveProductListing}
                />
            )}
        </>
    )
}

export default ProductListingDialog
